import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { usePullToRefresh } from '../../utils/pullToRefresh/usePullToRefresh';
import { PullToRefreshIndicator } from '../../utils/pullToRefresh/PullToRefreshIndicator';
import { useCheckInOverview, deriveCheckInData } from "../../hooks/useCheckInReports";
import { useDesktopTopBar } from "../../context/DesktopLayoutContext";
import { useDrillHistoryBack } from "../../hooks/useDrillHistoryBack";
import DesktopLayout from "../layout/DesktopLayout";
import TopBar from "../layout/TopBar";
import BottomNav from "../BottomNav";
import { getInitialReportDateFilter } from "../../utils/reportDateFilter";
import { comparisonTooltip } from "../../utils/reportPeriod";
import { getStatusPill } from "../../utils/statusPill";
import { getLiveCheckInTable, getLiveCheckInToday } from "../../services/reportsApi";
import "./reports.css";
import "./CheckinReport.scss";
import { T, useIsDesktop, Bone } from "./reportTokens";
import SectionError from "./SectionError";
import SectionEmpty from "./SectionEmpty";
import HourlyBarChart from "./components/charts/reusable/HourlyBarChart";
import { useSwipeBack } from '../../hooks/useSwipeBack';

/* ═══════════════════════════════════════════════════════════════════════════
   MAGHIL ANALYTICS — CHECK-IN REPORT
   Responsive: Desktop (≥900px) + Mobile (<900px) — API-driven
   ═══════════════════════════════════════════════════════════════════════════ */

function CheckinDesktopShimmer({ isToday = false }: { isToday?: boolean }) {
    const kpiLabels = isToday
        ? ["TOTAL ACTIVE", "BOOKED", "CONFIRMED", "ARRIVED", "LATE ARRIVAL"]
        : ["TOTAL CHECK-INS", "TOTAL GUESTS", "AVG PARTY SIZE", "CANCELLATION RATE", "TOTAL CANCELLATIONS", "AVG WAIT TIME", "AVG DINE-IN", "PEAK HOUR"];
    const kpiGridCols = isToday ? "repeat(3, minmax(0, 1fr))" : "repeat(auto-fit, minmax(180px, 1fr))";
    return (
        <div className="rpt-page">
            {/* KPI row */}
            <div style={{ display: "grid", gap: 16, gridTemplateColumns: kpiGridCols }}>
                {kpiLabels.map((label) => (
                    <div key={label} className="rpt-tile" style={{ minWidth: 0, background: T.card, border: `1px solid ${T.cardBorder}`, boxShadow: T.cardShadow }}>
                        <div className="rpt-tile__label" style={{ marginBottom: 8 }}>{label}</div>
                        <Bone h={32} r={6} mb={6} />
                        <Bone h={14} w={80} r={4} />
                    </div>
                ))}
            </div>
            {isToday ? (
                /* Live Today Check-In Tables placeholder */
                <div className="rpt-card rpt-card--r16">
                    <div className="rpt-section-title">Live Check-Ins</div>
                    {[1, 2, 3, 4, 5].map((i) => (
                        <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: i < 5 ? `1px solid ${T.divider}` : "none" }}>
                            <Bone w={120} h={12} r={4} />
                            <Bone w={80} h={12} r={4} />
                            <Bone w={60} h={12} r={4} />
                        </div>
                    ))}
                </div>
            ) : (
                <>
                    {/* Guest Volume & Hourly summary row */}
                    <div className="rpt-flex-row rpt-gap-16">
                        <div className="rpt-card rpt-card--r16" style={{ flex: 2 }}>
                            <div className="rpt-section-title">Guest Volume & Check-In Trend</div>
                            <Bone h={160} r={8} />
                        </div>
                        <div className="rpt-card rpt-card--r16" style={{ flex: 1 }}>
                            <div className="rpt-section-title">Hourly Summary</div>
                            <Bone h={160} r={8} />
                        </div>
                    </div>
                    {/* Hourly Volume (full width) */}
                    <div className="rpt-flex-row rpt-gap-16">
                        <div className="rpt-card rpt-card--r16" style={{ flex: 1 }}>
                            <div className="rpt-section-title">Hourly Check-In Volume</div>
                            <Bone h={160} r={8} />
                        </div>
                    </div>
                    {/* Party Size Distribution + Wait Time */}
                    <div className="rpt-flex-row rpt-gap-16">
                        <div className="rpt-card rpt-card--r16" style={{ flex: 1 }}>
                            <div className="rpt-section-title">Party Size Distribution</div>
                            <Bone h={140} r={8} />
                        </div>
                        <div className="rpt-card rpt-card--r16" style={{ flex: 1 }}>
                            <div className="rpt-section-title">Wait Time by Party Size</div>
                            <Bone h={140} r={8} />
                        </div>
                    </div>
                    {/* Dine-In Duration row */}
                    <div className="rpt-flex-row rpt-gap-16">
                        <div className="rpt-card rpt-card--r16" style={{ flex: 1 }}>
                            <div className="rpt-section-title">Dine-In Duration by Party Size</div>
                            <Bone h={200} r={8} />
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

function CheckinMobileShimmer({ isToday = false }: { isToday?: boolean }) {
    const kpiLabels = isToday
        ? ["TOTAL ACTIVE", "BOOKED", "CONFIRMED", "ARRIVED", "LATE ARRIVAL"]
        : ["TOTAL CHECK-INS", "TOTAL GUESTS", "AVG PARTY SIZE", "CANCEL RATE", "AVG WAIT TIME", "AVG DINE-IN"];
    const cols = isToday ? "1fr 1fr" : "1fr 1fr 1fr";
    const rowSize = isToday ? 2 : 3;
    const rows: string[][] = [];
    for (let i = 0; i < kpiLabels.length; i += rowSize) rows.push(kpiLabels.slice(i, i + rowSize));
    return (
        <div style={{ flex: 1, overflowY: "auto", padding: "8px 16px 88px" }}>
            {rows.map((row, ri) => (
                <div key={ri} style={{ display: "grid", gridTemplateColumns: cols, gap: 8, marginBottom: ri === rows.length - 1 ? 12 : 8 }}>
                    {row.map((label) => (
                        <div key={label} style={{ background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 12, padding: "12px 10px", textAlign: "center", boxShadow: T.shadow }}>
                            <div style={{ fontSize: 8, color: T.t3, textTransform: "uppercase", letterSpacing: 0.3, marginBottom: 4 }}>{label}</div>
                            <Bone h={20} r={5} />
                        </div>
                    ))}
                </div>
            ))}
            {isToday ? (
                /* Live Today Check-In Tables placeholder */
                <div style={{ background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 14, padding: 14, boxShadow: T.shadow }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: T.t1, marginBottom: 10 }}>Live Check-Ins</div>
                    {[1, 2, 3, 4].map((i) => (
                        <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: i < 4 ? `1px solid ${T.divider}` : "none" }}>
                            <Bone w={100} h={11} r={4} />
                            <Bone w={55} h={11} r={4} />
                        </div>
                    ))}
                </div>
            ) : (
                <>
                    <div style={{ background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 14, padding: 14, boxShadow: T.shadow, marginBottom: 8 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: T.t1, marginBottom: 10 }}>Guest Volume & Check-In Trend</div>
                        <Bone h={120} r={8} />
                    </div>
                    <div style={{ background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 14, padding: 14, boxShadow: T.shadow, marginBottom: 8 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: T.t1, marginBottom: 10 }}>Hourly Volume</div>
                        <Bone h={80} r={8} />
                    </div>
                    <div style={{ background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 14, padding: 14, boxShadow: T.shadow, marginBottom: 8 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: T.t1, marginBottom: 10 }}>Party Sizes</div>
                        <Bone h={70} r={8} />
                    </div>
                    <div style={{ background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 14, padding: 14, boxShadow: T.shadow }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: T.t1, marginBottom: 10 }}>Wait Time by Party Size</div>
                        <Bone h={70} r={8} />
                    </div>
                </>
            )}
        </div>
    );
}

const DRILLABLE = ["checkins","guests","avgParty","cancels","waitTime","dinein"];
const MOBILE_DRILLABLE = ["checkins","guests","avgParty","cancels","waitTime","dinein"];
const DRILL_TITLES = {
    checkins:"Total Check-Ins", guests:"Total Guests", avgParty:"Avg Party Size",
    cancels:"Cancellations",    waitTime:"Avg Wait Time", dinein:"Avg Dine-In Duration",
};
const M_DRILL_HEADERS = {
    checkins: { title: "Total Check-Ins", breadcrumb: "Check-In Analytics › Check-Ins", getSubtitle: (d) => `${d?.kpis?.find(k=>k.key==="checkins")?.value ?? "0"} parties this period` },
    guests:   { title: "Total Guests", breadcrumb: "Check-In Analytics › Guests", getSubtitle: (d) => `${d?.kpis?.find(k=>k.key==="guests")?.value ?? "0"} guests this period` },
    avgParty: { title: "Avg Party Size", breadcrumb: "Check-In Analytics › Party Size", getSubtitle: (d) => `${d?.kpis?.find(k=>k.key==="avgParty")?.value ?? "—"} guests per party` },
    cancels:  { title: "Cancellations", breadcrumb: "Check-In Analytics › Cancellations", getSubtitle: (d) => `${d?.kpis?.find(k=>k.key==="totalCancel")?.value ?? "0"} cancellations (${d?.kpis?.find(k=>k.key==="cancels")?.value ?? "0"} rate)` },
    waitTime: { title: "Avg Wait Time", breadcrumb: "Check-In Analytics › Wait Time", getSubtitle: (d) => d?.kpis?.find(k=>k.key==="waitTime")?.value ? `${d.kpis.find(k=>k.key==="waitTime").value} average this period` : "No data" },
    dinein:   { title: "Avg Dine-In Duration", breadcrumb: "Check-In Analytics › Dine-In", getSubtitle: () => "Duration by group size" },
};

/** Scroll the page (and any scrollable ancestors of the report root) to the top — used when entering a drill view */
function scrollPageToTop() {
    window.scrollTo({ top: 0, behavior: 'auto' });
    const root = document.querySelector('.checkin-report-root');
    let el: Element | null = root;
    while (el) {
        const { overflowY } = window.getComputedStyle(el);
        if ((overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight) {
            (el as HTMLElement).scrollTo({ top: 0, behavior: 'auto' });
        }
        el = el.parentElement;
    }
}

/** Scroll to a target element by id — finds the nearest scrollable ancestor, falls back to window */
function scrollToTarget(targetId: string) {
    const el = document.getElementById(targetId);
    if (!el) return;
    // Walk up to find a scrollable ancestor
    let scrollContainer: Element | null = el.parentElement;
    while (scrollContainer) {
        const { overflowY } = window.getComputedStyle(scrollContainer);
        if ((overflowY === 'auto' || overflowY === 'scroll') && scrollContainer.scrollHeight > scrollContainer.clientHeight) break;
        scrollContainer = scrollContainer.parentElement;
    }
    if (scrollContainer) {
        const containerRect = scrollContainer.getBoundingClientRect();
        const elRect = el.getBoundingClientRect();
        const scrollTop = scrollContainer.scrollTop + (elRect.top - containerRect.top) - (containerRect.height / 3);
        scrollContainer.scrollTo({ top: Math.max(0, scrollTop), behavior: 'smooth' });
    } else {
        // No scrollable ancestor — use window scroll
        const elRect = el.getBoundingClientRect();
        const scrollTop = window.scrollY + elRect.top - (window.innerHeight / 3);
        window.scrollTo({ top: Math.max(0, scrollTop), behavior: 'smooth' });
    }
}

/* ═══════════════════════════════════════════════════════════════════════════
   SHARED CHART PRIMITIVES (used by both layouts)
   ═══════════════════════════════════════════════════════════════════════════ */
function GuestVolumeSVG({ data = [], height = 160, labelStep = 5, strokeW = 2, isDesktop = true, compact = false }: any) {
    const n = data.length;
    if (!n) return <svg width="100%" viewBox={`0 0 600 ${height}`} style={{ display:"block" }}><text x={300} y={height/2} textAnchor="middle" fontSize={12} fill={T.t3}>No data</text></svg>;
    const allVals = [...data.map(d => Number(d.guests) || 0), ...data.map(d => Number(d.checkins) || 0)];
    const maxV = Math.max(1, ...allVals.filter((v) => Number.isFinite(v)));
    const niceStep = (max: number, targetTicks = 5) => {
        const rough = max / targetTicks;
        const mag = Math.pow(10, Math.floor(Math.log10(rough)));
        const norm = rough / mag;
        const nice = norm < 1.5 ? 1 : norm < 3 ? 2 : norm < 7 ? 5 : 10;
        return Math.max(1, nice * mag);
    };
    const step = niceStep(maxV);
    const yTicks: number[] = [];
    for (let v = 0; v <= maxV + step && yTicks.length < 8; v += step) yTicks.push(v);
    while (yTicks.length > 0 && yTicks[yTicks.length - 1] < maxV) yTicks.push(yTicks[yTicks.length - 1] + step);
    const scaleMax = Math.max(maxV, yTicks.length ? yTicks[yTicks.length - 1] : maxV);
    const w = 600;
    const pad = { top: 20, right: 28, bottom: isDesktop ? 28 : 26, left: 48 };
    const cW = w - pad.left - pad.right;
    const cH = height - pad.top - pad.bottom;
    const insetX = n > 1 ? Math.min(24, cW * 0.05) : 0;
    const toX = (i: any) => pad.left + (n === 1 ? cW / 2 : insetX + (i / Math.max(n - 1, 1)) * (cW - 2 * insetX));
    const toY = (v: any) => pad.top + cH - ((Number(v) || 0) / scaleMax) * cH;
    const gLine = data.map((d, i) => `${i === 0 ? "M" : "L"}${toX(i).toFixed(1)},${toY(d.guests).toFixed(1)}`).join(" ");
    const cLine = data.map((d, i) => `${i === 0 ? "M" : "L"}${toX(i).toFixed(1)},${toY(d.checkins).toFixed(1)}`).join(" ");
    const area  = gLine + ` L${toX(n-1).toFixed(1)},${(pad.top+cH).toFixed(1)} L${toX(0).toFixed(1)},${(pad.top+cH).toFixed(1)} Z`;
    // x-axis labels: sampled to avoid crowding
    const xAxisIdx = n <= 14 ? data.map((_, i) => i) : data.map((_, i) => i).filter(i => i % labelStep === 0 || i === n - 1);
    // dots: sampled + all non-zero so peaks are always marked with a dot
    const xDotIdx = n <= 14
        ? data.map((_, i) => i)
        : [...new Set([
            ...data.map((_, i) => i).filter(i => i % labelStep === 0 || i === n - 1),
            ...data.map((_, i) => i).filter(i => (Number(data[i].guests) || 0) > 0 || (Number(data[i].checkins) || 0) > 0),
          ])].sort((a, b) => a - b);
    // value labels: always show at every dot (non-zero peaks are already filtered into xDotIdx)
    const xLabelIdx = new Set(xDotIdx);
    const labelFontSize = compact ? (isDesktop ? 6 : 16) : (isDesktop ? 11 : 13);
    const labelOffsetNear = compact ? (isDesktop ? 5 : 12) : (isDesktop ? 6 : 8);
    const stackGap = labelFontSize + 3;
    const xAxisLineY = pad.top + cH;
    return (
        <svg width="100%" viewBox={`0 0 ${w} ${height}`} style={{ display:"block" }}>
            <defs>
                <linearGradient id="gvArea" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={T.accent} stopOpacity="0.15"/>
                    <stop offset="100%" stopColor={T.accent} stopOpacity="0.01"/>
                </linearGradient>
            </defs>
            {yTicks.map(v => (
                <g key={v}>
                    <line x1={pad.left} y1={toY(v)} x2={w-pad.right} y2={toY(v)} stroke={T.divider} strokeWidth="0.5"/>
                    <text x={pad.left-4} y={toY(v)+4} textAnchor="end" fontSize={compact ? (isDesktop ? "7" : "18") : "13"} fill={T.t3} fontFamily={T.font}>{Math.round(v)}</text>
                </g>
            ))}
            <path d={area} fill="url(#gvArea)"/>
            <path d={gLine} fill="none" stroke={T.accent} strokeWidth={strokeW} strokeLinecap="round" strokeLinejoin="round"/>
            <path d={cLine} fill="none" stroke={T.green}  strokeWidth={strokeW * 0.75} strokeLinecap="round" strokeLinejoin="round" strokeDasharray="5 3"/>
            {/* Dot markers at all sampled + non-zero positions; value labels only at positions in xLabelIdx */}
            {xDotIdx.map(i => {
                const gY = toY(data[i].guests);
                const cY = toY(data[i].checkins);
                const gVal = Number(data[i].guests) || 0;
                const cVal = Number(data[i].checkins) || 0;
                const showLabel = xLabelIdx.has(i);
                // Always stack labels upward — never push them downward toward the x-axis
                const gAbove = gY <= cY;
                let gLabelY: number;
                let cLabelY: number;
                if (Math.abs(gY - cY) < stackGap) {
                    // Both dots are close: place the higher dot's label first, stack the other above it
                    const topDotY = Math.min(gY, cY);
                    if (gAbove) {
                        gLabelY = topDotY - labelOffsetNear;
                        cLabelY = gLabelY - stackGap;
                    } else {
                        cLabelY = topDotY - labelOffsetNear;
                        gLabelY = cLabelY - stackGap;
                    }
                } else {
                    gLabelY = gY - labelOffsetNear;
                    cLabelY = cY - labelOffsetNear;
                }
                // Clamp labels to never go below the x-axis line
                const minLabelY = xAxisLineY - labelFontSize - 2;
                gLabelY = Math.min(gLabelY, minLabelY);
                cLabelY = Math.min(cLabelY, minLabelY);
                return (
                    <g key={`dl-${i}`}>
                        <circle cx={toX(i)} cy={gY} r="3" fill={T.accent} stroke="#fff" strokeWidth="1.5"/>
                        <circle cx={toX(i)} cy={cY} r="2.5" fill={T.green} stroke="#fff" strokeWidth="1.5"/>
                        {showLabel && gVal > 0 && <text x={toX(i)} y={gLabelY} textAnchor="middle" fontSize={labelFontSize} fontWeight="400" fill={T.accent} fontFamily={T.font} stroke="#fff" strokeWidth="1.5" paintOrder="stroke">{gVal}</text>}
                        {showLabel && cVal > 0 && <text x={toX(i)} y={cLabelY} textAnchor="middle" fontSize={labelFontSize} fontWeight="400" fill={T.green} fontFamily={T.font} stroke="#fff" strokeWidth="1.5" paintOrder="stroke">{cVal}</text>}
                    </g>
                );
            })}
            {/* X-axis day/hour labels: sampled only to avoid crowding */}
            {xAxisIdx.map(i => (
                <text key={i} x={toX(i)} y={height-4} textAnchor="middle" fontSize={compact ? (isDesktop ? "7" : "18") : "13"} fill={T.t3} fontFamily={T.font}>{String(data[i].d ?? '').includes('NaN') ? '—' : (data[i].d ?? '—')}</text>
            ))}
        </svg>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   UNIFIED SHARED COMPONENTS (responsive: desktop vs mobile)
   ═══════════════════════════════════════════════════════════════════════════ */
function ChartCard({ id, title, subtitle, children, drillable, onTap, flex = 1, minHeight = 220, alignSelf }: any) {
    const isDesktop = useIsDesktop();
    if (isDesktop) {
        return (
            <div
                id={id}
                onClick={onTap}
                className="rpt-card"
                style={{ flex, borderRadius: 14, minHeight, alignSelf, position: "relative", cursor: onTap ? "pointer" : "default", border: drillable ? `1px solid ${T.accent}33` : undefined }}
            >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
                    <div>
                        <div style={{ fontSize: 14, fontWeight: 600, color: T.t1 }}>{title}</div>
                        {subtitle && <div style={{ fontSize: 11, color: T.t3, marginTop: 2 }}>{subtitle}</div>}
                    </div>
                    {drillable && <span className="material-symbols-rounded" style={{ fontSize: 16, color: T.accent, opacity: 0.45, flexShrink: 0, marginLeft: 8 }}>chevron_right</span>}
                </div>
                {children}
            </div>
        );
    }
    return (
        <div
            id={id}
            onClick={onTap}
            className={`checkin-mobile-card ${drillable ? "checkin-mobile-card--drillable" : ""}`}
            style={{ border: `1px solid ${drillable ? `${T.accent}33` : T.cardBorder}`, cursor: onTap ? "pointer" : "default" }}
        >
            {drillable && <span className="material-symbols-rounded" style={{ position: "absolute", top: 10, right: 10, fontSize: 14, color: T.accent, opacity: 0.45 }}>chevron_right</span>}
            <div style={{ fontSize: 12, fontWeight: 600, color: T.t1 }}>{title}</div>
            {subtitle && <div style={{ fontSize: 9, color: T.t3, marginTop: 1, marginBottom: 10 }}>{subtitle}</div>}
            {!subtitle && <div style={{ marginBottom: 10 }} />}
            {children}
        </div>
    );
}

function parseWaitTimeForBar(waitTime: any) {
    if (waitTime == null) return 0;
    const raw = String(waitTime).trim();
    if (!raw) return 0;
    if (raw.includes("-")) {
        const parts = raw.split("-").map((x) => Number(String(x).replace(/[^\d.]/g, "")));
        const upper = parts[1];
        const lower = parts[0];
        if (Number.isFinite(upper)) return upper;
        if (Number.isFinite(lower)) return lower;
    }
    const val = Number(raw.replace(/[^\d.]/g, ""));
    return Number.isFinite(val) ? val : 0;
}

function getWaitByPartySizeChartData(data: any) {
    const raw = data?.avgWaitTimeByGroup || [];
    if (raw.length) {
        return raw.map((row) => ({
            s: row?.size ?? "",
            w: parseWaitTimeForBar(row?.waitTime),
            waitLabel: row?.waitTime || "—",
        }));
    }
    return (data?.partySize || []).map((row) => ({
        s: row?.size,
        w: Number(row?.wait) || 0,
        waitLabel: `${Number(row?.wait) || 0}`,
    }));
}

function parseHourLabelTo24(label: string): number {
    const raw = String(label || "").trim();
    const m = raw.match(/^(\d{1,2})\s*(AM|PM)$/i);
    if (!m) return Number.POSITIVE_INFINITY;
    const n = Number(m[1]);
    const ap = m[2].toUpperCase();
    if (!Number.isFinite(n) || n < 1 || n > 12) return Number.POSITIVE_INFINITY;
    if (ap === "AM") return n === 12 ? 0 : n;
    return n === 12 ? 12 : n + 12;
}

function hexToRgba(hex: string, alpha: number): string {
    const clean = hex.replace('#', '');
    const full = clean.length === 3 ? clean.split('').map(c => c + c).join('') : clean;
    const r = parseInt(full.slice(0, 2), 16);
    const g = parseInt(full.slice(2, 4), 16);
    const b = parseInt(full.slice(4, 6), 16);
    return `rgba(${r},${g},${b},${alpha})`;
}

function BarChart({ data, valueKey, labelKey, height = 80, color = T.accent, threshold, showValues = true, displayValueKey }: any) {
    const isDesktop = useIsDesktop();
    const [hoverIdx, setHoverIdx] = useState<number>(-1);
    const maxV = Math.max(...data.map(d => d[valueKey]), 0);
    const n = data.length;
    const dense = !isDesktop && n > 13;
    const showValueLabels = showValues && !dense;
    const valueSlot = showValueLabels ? (isDesktop ? 18 : 14) : 0;
    const labelSlot = isDesktop ? 20 : (dense ? 12 : 16);
    const chartHeight = Math.max(24, height - valueSlot - labelSlot);
    const plotHeight = chartHeight + valueSlot;
    const barHeight = (v: any) => maxV > 0 ? (Number(v) / maxV) * chartHeight : 0;
    const gap = isDesktop ? 3 : (dense ? 1 : 2);
    const maxBarW = isDesktop ? 24 : 18;
    const labelSz = isDesktop ? 9 : (dense ? 5 : 7);
    const valueSz = isDesktop ? 9 : 7;
    const nonZero = data.filter((d: any) => Number(d[valueKey]) > 0);
    const avgV = nonZero.length > 0 ? nonZero.reduce((s: number, d: any) => s + Number(d[valueKey]), 0) / nonZero.length : 0;
    const showAvgLine = avgV > 0 && maxV > 0 && nonZero.length >= 2 && avgV < maxV;
    const useRankedColors = n >= 8;
    const sortedByVal = useRankedColors
        ? [...data].map((d: any, i: number) => ({ i, v: Number(d[valueKey]) || 0 })).sort((a, b) => b.v - a.v)
        : [];
    const rank1Idx = sortedByVal[0]?.i ?? -1;
    const rank2Idx = sortedByVal[1]?.i ?? -1;
    const rank3Idx = sortedByVal[2]?.i ?? -1;
    const avgDisplay = avgV >= 10 ? Math.round(avgV).toLocaleString() : avgV.toFixed(1);
    return (
        <div style={{ position: "relative", display: "flex", alignItems: "stretch", gap, height }} onMouseLeave={() => setHoverIdx(-1)}>
            {showAvgLine && (
                <div style={{ position: "absolute", left: 0, right: 0, bottom: `${labelSlot + (avgV / maxV) * chartHeight}px`, borderTop: `1px dashed ${T.t3}`, pointerEvents: "none", zIndex: 1 }}>
                    <div style={{ position: "absolute", right: 0, top: isDesktop ? -8 : -7, fontSize: isDesktop ? 9 : 8, color: T.t2, background: T.surface, padding: "0 3px", fontWeight: 500, whiteSpace: "nowrap" }}>Avg: {avgDisplay}</div>
                </div>
            )}
            {data.map((d, i) => {
                const v = d[valueKey];
                const h = barHeight(v);
                const effectiveH = h > 0 ? Math.max(h, 20) : 0;
                const isPeak = v === maxV;
                const over = threshold && v > threshold;
                const clampedLabelBottom = Math.max(
                    0,
                    Math.min(effectiveH + 3, plotHeight - (isDesktop ? 12 : 10))
                );
                const showLabel = true;
                const isHovered = i === hoverIdx && v > 0;
                const tooltipValue = displayValueKey ? d[displayValueKey] : v;
                return (
                    <div
                        key={i}
                        style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-end", cursor: v > 0 ? "pointer" : "default", position: "relative", zIndex: isHovered ? 40 : "auto" }}
                        onMouseEnter={() => setHoverIdx(i)}
                        onTouchStart={() => setHoverIdx(i)}
                    >
                        <div style={{ height: plotHeight, width: "100%", position: "relative", display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
                            {isHovered && (
                                <div style={{ position: "absolute", bottom: effectiveH + 4, left: "50%", transform: "translateX(-50%)", background: T.surface, border: `1px solid ${T.cardBorder}`, borderRadius: 6, padding: "4px 8px", boxShadow: "0 4px 12px rgba(0,0,0,0.10)", fontSize: isDesktop ? 11 : 10, color: T.t1, pointerEvents: "none", zIndex: 20, whiteSpace: "nowrap", lineHeight: 1.3 }}>
                                    <div style={{ fontWeight: 500, color: T.t3, fontSize: isDesktop ? 10 : 9 }}>{d[labelKey]}</div>
                                    <div style={{ fontWeight: 700, color: over ? T.accent : color }}>{tooltipValue}</div>
                                </div>
                            )}
                            {showValueLabels && (
                                <div style={{ position: "absolute", bottom: clampedLabelBottom, left: 0, right: 0, textAlign: "center", fontSize: valueSz, fontWeight: 600, color: over ? T.accent : isPeak ? color : T.t3, lineHeight: 1 }}>
                                    {displayValueKey ? d[displayValueKey] : v}
                                </div>
                            )}
                            <div style={{ width: "100%", maxWidth: maxBarW, height: effectiveH, borderRadius: "3px 3px 0 0", background: (() => {
                                if (over) return T.accent;
                                if (useRankedColors) {
                                    if (i === rank1Idx) return color;
                                    if (isHovered) return hexToRgba(color, 0.85);
                                    if (i === rank2Idx) return hexToRgba(color, 0.75);
                                    if (i === rank3Idx) return hexToRgba(color, 0.55);
                                    return hexToRgba(color, 0.40);
                                }
                                return isPeak ? color : (isHovered ? color : `${color}66`);
                            })(), transition: "background 0.15s", position: "relative" }} />
                        </div>
                        <div style={{ height: labelSlot, display: "flex", alignItems: "flex-end", justifyContent: "center", fontSize: labelSz, color: isPeak ? T.t1 : T.t3, fontWeight: isPeak ? (isDesktop ? 600 : 700) : 400, overflow: "hidden", whiteSpace: "nowrap" }}>
                            {showLabel ? d[labelKey] : ""}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

function ProgressList({ data, labelKey, valueKey, valueSuffix = "", color = T.accent, labelWidth }: any) {
    const isDesktop = useIsDesktop();
    const maxV = Math.max(...data.map(d => d[valueKey]));
    const gap = isDesktop ? 10 : 8;
    const fontSize = isDesktop ? 12 : 11;
    const barH = isDesktop ? 6 : 5;
    const lw = labelWidth ?? (isDesktop ? 90 : undefined);
    return (
        <div style={{ display: "flex", flexDirection: "column", gap }}>
            {data.map((d, i) => (
                <div key={i}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: isDesktop ? 4 : 3 }}>
                        <span style={{ fontSize, color: T.t2, ...(lw != null && { width: lw }) }}>{d[labelKey]}</span>
                        <span style={{ fontSize, fontWeight: 600, color: T.t1 }}>{d[valueKey]}{valueSuffix}</span>
                    </div>
                    <div style={{ height: barH, background: T.hover, borderRadius: 3, overflow: "hidden" }}>
                        <div style={{ height: "100%", width: `${maxV > 0 ? (d[valueKey] / maxV) * 100 : 0}%`, borderRadius: 3, background: `${color}AA` }} />
                    </div>
                </div>
            ))}
        </div>
    );
}

function AvgWaitByGroupTable({ rows = [], rowsPerPage = 5, compact = false }: any) {
    const isDesktop = useIsDesktop();
    const pageSize = Math.max(1, rowsPerPage);
    const [page, setPage] = useState(0);
    const dataRows = useMemo(() => {
        const parseSort = (size: any) => {
            if (!size) return Number.POSITIVE_INFINITY;
            if (String(size).includes("+")) return 999;
            const num = Number(String(size).replace(/[^\d]/g, ""));
            return Number.isFinite(num) ? num : Number.POSITIVE_INFINITY;
        };
        return [...(rows || [])]
            .map((row) => {
                const size = row?.size != null ? String(row.size) : "";
                return {
                    size,
                    groupLabel: row?.groupLabel || row?.label || (size ? `Group of ${size}` : "Group"),
                    checkInCount: Number(row?.checkInCount ?? row?.count) || 0,
                    waitTime: row?.waitTime || (row?.wait ? `${row.wait} min` : "—"),
                    sortValue: parseSort(size),
                };
            })
            .filter((row) => row.groupLabel)
            .sort((a, b) => a.sortValue - b.sortValue);
    }, [rows]);
    const total = dataRows.length;
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const safePage = Math.min(page, totalPages - 1);
    const start = total === 0 ? 0 : (safePage * pageSize) + 1;
    const end = Math.min((safePage + 1) * pageSize, total);
    const visibleRows = dataRows.slice(safePage * pageSize, (safePage + 1) * pageSize);

    useEffect(() => {
        setPage(0);
    }, [total, pageSize]);

    if (!visibleRows.length) {
        return <div style={{ color: T.t3, fontSize: isDesktop ? 12 : 10 }}>No group wait-time data for selected period.</div>;
    }

    const cell = { fontSize: isDesktop ? 13 : 11, color: T.t1, padding: isDesktop ? "14px 0" : "10px 0", borderBottom: `1px solid ${T.divider}` };
    const headerCell = { fontSize: isDesktop ? 11 : 9, color: T.t3, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4, paddingBottom: isDesktop ? 10 : 8 };

    return (
        <div className="checkin-wait-table">
            <div className="checkin-wait-table__header" style={{ borderBottom: `1px solid ${T.divider}` }}>
                <div style={headerCell}>Group size</div>
                <div style={headerCell}>CheckIn count</div>
                <div style={headerCell}>Wait time</div>
            </div>
            {visibleRows.map((row, i) => (
                <div key={`${row.groupLabel}-${i}`} className="checkin-wait-table__row">
                    <div style={{ ...cell, fontWeight: 500 }}>{row.groupLabel}</div>
                    <div style={cell}>{row.checkInCount}</div>
                    <div style={cell}>{row.waitTime}</div>
                </div>
            ))}
            <div className="checkin-wait-table__pager" style={{ marginTop: isDesktop ? 12 : 10 }}>
                <span style={{ fontSize: isDesktop ? 12 : 10, color: T.t2 }}>{start}-{end} of {total}</span>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <button type="button" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={safePage === 0} style={{ border: `1px solid ${T.cardBorder}`, background: T.surface, borderRadius: 8, width: compact ? 34 : 40, height: compact ? 32 : 36, color: safePage === 0 ? T.t3 : T.t2, cursor: safePage === 0 ? "not-allowed" : "pointer" }}>
                        <span className="material-symbols-rounded" style={{ fontSize: compact ? 16 : 18 }}>arrow_back</span>
                    </button>
                    <div style={{ minWidth: 32, height: compact ? 32 : 36, borderRadius: 8, border: `1px solid ${T.cardBorder}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: isDesktop ? 13 : 11, color: T.t1, background: T.surface }}>{safePage + 1}</div>
                    <button type="button" onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={safePage >= totalPages - 1} style={{ border: `1px solid ${T.cardBorder}`, background: T.surface, borderRadius: 8, width: compact ? 34 : 40, height: compact ? 32 : 36, color: safePage >= totalPages - 1 ? T.t3 : T.t2, cursor: safePage >= totalPages - 1 ? "not-allowed" : "pointer" }}>
                        <span className="material-symbols-rounded" style={{ fontSize: compact ? 16 : 18 }}>arrow_forward</span>
                    </button>
                </div>
            </div>
        </div>
    );
}

/**
 * GLOBAL-DP-4 (ZAR-1663): the per-card comparison label is removed — the basis is stated
 * once in the unified date control. Each delta keeps the resolved comparison period as a
 * hover tooltip so the basis stays recoverable per card.
 */
const getPriorLabel = (datePeriod: string) => comparisonTooltip(datePeriod || 'Today');

function KPICard({ id, label, value, delta, icon, invertDelta, drillable, onTap, deltaLabel }: any) {
    const isDesktop = useIsDesktop();
    const isPeakHourCard = String(label || '').trim().toUpperCase() === 'PEAK HOUR';
    const hasDelta = !isPeakHourCard && delta !== null;
    const safeDelta = delta == null ? 0 : Number(delta) || 0;
    const isPositive = invertDelta ? safeDelta < 0 : safeDelta > 0;
    const deltaColor = safeDelta === 0 ? T.t3 : (isPositive ? T.green : T.red);
    const deltaText = safeDelta === 0 ? "0%" : `${isPositive ? "↑" : "↓"} ${Math.abs(safeDelta).toFixed(1)}%`;
    // Comparison basis as a tooltip, not as trailing card text (GLOBAL-DP-4).
    const vsLabel = deltaLabel || '';
    if (isDesktop) {
        const blurColor = delta === null ? T.hover : isPositive ? "rgba(22,163,74,0.06)" : "rgba(220,38,38,0.06)";
        return (
            <div
                id={id}
                onClick={onTap}
                className={`checkin-kpi-card ${drillable ? "checkin-kpi-card--drillable" : ""}`}
                style={{ border: `1px solid ${drillable ? `${T.accent}33` : T.cardBorder}`, cursor: drillable ? "pointer" : "default" }}
            >
                <div style={{ position: "absolute", top: -20, right: -20, width: 80, height: 80, borderRadius: "50%", background: blurColor, filter: "blur(20px)" }} />
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, position: "relative" }}>
                    <span style={{ fontSize: 12, fontWeight: 500, color: T.t2, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</span>
                    {icon && <span className="material-symbols-rounded" style={{ fontSize: 20, color: T.t3 }}>{icon}</span>}
                </div>
                <div style={{ fontSize: 28, fontWeight: 700, color: T.t1, letterSpacing: -0.5, position: "relative" }}>{value}</div>
                {hasDelta && (
                    <div title={vsLabel} style={{ fontSize: 12, fontWeight: 600, color: deltaColor, marginTop: 4, position: "relative" }}>
                        {deltaText}
                    </div>
                )}
                {drillable && <span className="material-symbols-rounded" style={{ position: "absolute", bottom: 12, right: 14, fontSize: 16, color: T.accent, opacity: 0.3 }}>chevron_right</span>}
            </div>
        );
    }
    return (
        <div
            id={id}
            onClick={onTap}
            className="checkin-kpi-card-mobile"
            style={{ border: `1px solid ${drillable ? `${T.accent}33` : T.cardBorder}`, borderRadius: hasDelta ? 12 : 10, padding: hasDelta ? "12px 10px" : "10px 10px", cursor: drillable ? "pointer" : "default" }}
        >
            {drillable && <span className="material-symbols-rounded" style={{ position: "absolute", top: 5, right: 5, fontSize: 12, color: T.accent, opacity: 0.45 }}>chevron_right</span>}
            <div style={{ fontSize: 8, fontWeight: 400, color: T.t3, textTransform: "uppercase", letterSpacing: 0.3 }}>{label}</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: T.t1, marginTop: hasDelta ? 4 : 3 }}>{value}</div>
            {hasDelta && <div title={vsLabel} style={{ fontSize: 9, fontWeight: 600, color: deltaColor, marginTop: 3 }}>{deltaText}</div>}
        </div>
    );
}

function GuestVolumeTrend({ data = [], hourlySummary = [], height, compact = false }: any) {
    const isDesktop = useIsDesktop();
    const trend = data || [];
    const hourly = hourlySummary || [];
    const isSingleDay = trend.length === 1 && hourly.length > 0;
    const chartData = isSingleDay
        ? [...hourly].reverse().map((h) => ({ d: h.hour, guests: Number(h.guests) || 0, checkins: Number(h.checkins) || 0 }))
        : trend;
    const subtitle = isSingleDay ? "Guests & check-ins by hour" : "Daily guests & check-ins";
    const h = height ?? (isDesktop ? 360 : 140);
    const labelStep = isDesktop ? 5 : 7;
    const strokeW = isDesktop ? 2.5 : 2;
    const legendSize = isDesktop ? { w: 18, gap: 6, fontSize: 11 } : { w: 14, gap: 4, fontSize: 9 };
    const hasData = Array.isArray(chartData) && chartData.some((r: any) => (Number(r?.guests) || 0) > 0 || (Number(r?.checkins) || 0) > 0);
    if (!hasData) {
        return (
            <ChartCard title="Guest Volume Trend" subtitle={subtitle} flex={1} minHeight={460}>
                <SectionEmpty kpiName="guest volume" minHeight={isDesktop ? 320 : 140} />
            </ChartCard>
        );
    }
    return (
        <ChartCard title="Guest Volume Trend" subtitle={subtitle} flex={1}>
            <GuestVolumeSVG data={chartData} height={h} labelStep={labelStep} strokeW={strokeW} isDesktop={isDesktop} compact={compact} />
            <div style={{ display: "flex", gap: legendSize.gap * 2, marginTop: isDesktop ? 4 : 4, justifyContent: "center" }}>
                <div style={{ display: "flex", alignItems: "center", gap: legendSize.gap }}>
                    <div style={{ width: legendSize.w, height: isDesktop ? 3 : 2.5, background: T.accent, borderRadius: isDesktop ? 1.5 : 1 }} />
                    <span style={{ fontSize: legendSize.fontSize, color: T.t2 }}>Guests</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: legendSize.gap }}>
                    <svg width={legendSize.w} height="3"><line x1="0" y1="1.5" x2={legendSize.w} y2="1.5" stroke={T.green} strokeWidth={isDesktop ? 2 : 1.5} strokeDasharray={isDesktop ? "4 3" : "3 2"} /></svg>
                    <span style={{ fontSize: legendSize.fontSize, color: T.t2 }}>Check-Ins</span>
                </div>
            </div>
        </ChartCard>
    );
}

function HourlySummary({ data: summaryData = [] }: any) {
    const isDesktop = useIsDesktop();
    const raw = summaryData.length ? summaryData : [];
    const filtered = [...raw]
        .reverse()
        .filter((row) => (Number(row?.checkins) || 0) > 0 || (Number(row?.guests) || 0) > 0);
    const data = [...filtered].sort((a, b) => parseHourLabelTo24(a?.hour) - parseHourLabelTo24(b?.hour));
    if (!data.length) return <ChartCard title="Hourly Summary" subtitle="Guests, check-ins by hour"><SectionEmpty kpiName="hourly summary" minHeight={isDesktop ? 320 : 140} /></ChartCard>;
    if (!isDesktop) {
        const maxG = Math.max(...data.map(d => d.guests), 1);
        return (
            <ChartCard title="Hourly Summary" subtitle="Guests & check-ins by hour">
                <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
                    {[{ color: T.accent, label: "Guests" }, { color: T.green, label: "Check-Ins" }].map((l, i) => (
                        <div key={i} style={{ display: "flex", alignItems: "center", gap: 3 }}>
                            <div style={{ width: 6, height: 6, borderRadius: 2, background: l.color }} />
                            <span style={{ fontSize: 7, color: T.t2 }}>{l.label}</span>
                        </div>
                    ))}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {data.map((d, i) => {
                        const gPct = (d.guests / maxG) * 100;
                        const cPct = (d.checkins / maxG) * 100;
                        return (
                            <div key={i}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 3 }}>
                                    <span style={{ fontSize: 10, fontWeight: 600, color: T.t1 }}>{d.hour}</span>
                                </div>
                                <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 2 }}>
                                    <div style={{ flex: 1, height: 8, background: T.hover, borderRadius: 3, overflow: "hidden" }}>
                                        <div style={{ width: `${gPct}%`, height: "100%", borderRadius: 3, background: `${T.accent}CC` }} />
                                    </div>
                                    <span style={{ fontSize: 7, fontWeight: 600, color: T.accent, width: 20, textAlign: "right", flexShrink: 0 }}>{d.guests}</span>
                                </div>
                                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                    <div style={{ flex: 1, height: 6, background: T.hover, borderRadius: 3, overflow: "hidden" }}>
                                        <div style={{ width: `${cPct}%`, height: "100%", borderRadius: 3, background: `${T.green}BB` }} />
                                    </div>
                                    <span style={{ fontSize: 7, fontWeight: 600, color: T.green, width: 20, textAlign: "right", flexShrink: 0 }}>{d.checkins}</span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </ChartCard>
        );
    }
    const n = data.length;
    const maxGuests = Math.max(...data.map(d => d.guests), 1);
    const stepLeft = maxGuests <= 10 ? 2 : maxGuests <= 25 ? 5 : maxGuests <= 50 ? 10 : maxGuests <= 100 ? 25 : maxGuests <= 200 ? 50 : maxGuests <= 500 ? 100 : maxGuests <= 1000 ? 200 : maxGuests <= 2000 ? 250 : maxGuests <= 5000 ? 500 : 1000;
    const scaleMaxLeft = Math.ceil(maxGuests / stepLeft) * stepLeft || stepLeft;
    const yTicks = [];
    for (let v = 0; v <= scaleMaxLeft && yTicks.length < 10; v += stepLeft) yTicks.push(v);
    const w = 760, h = 430;
    const pad = { top: 16, right: 16, bottom: 20, left: 48 };
    const cW = w - pad.left - pad.right, cH = h - pad.top - pad.bottom;
    const barGroupW = cW / n, barW = 10, gap = 4;
    const toX = (i: any) => pad.left + i * barGroupW + barGroupW / 2;
    const toYL = (v: any) => pad.top + cH - (v / scaleMaxLeft) * cH;
    return (
        <div className="rpt-card" style={{ borderRadius: 14, paddingBottom: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
                <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span className="material-symbols-rounded" style={{ fontSize: 18, color: T.t3 }}>monitoring</span>
                        <span style={{ fontSize: 16, fontWeight: 700, color: T.t1 }}>Hourly Check-In Summary</span>
                    </div>
                    <div style={{ fontSize: 12, color: T.t3, marginTop: 2, marginLeft: 24 }}>Aggregated by hour for the selected period</div>
                </div>
            </div>
            <svg width="100%" viewBox={`0 0 ${w} ${h}`} style={{ display: "block" }}>
                {yTicks.map(v => { const y = toYL(v); return (<g key={v}><line x1={pad.left} y1={y} x2={w - pad.right} y2={y} stroke={T.divider} strokeWidth="0.5" /><text x={pad.left - 8} y={y + 4} textAnchor="end" fontSize="11" fill={T.t3} fontFamily={T.font}>{v}</text></g>); })}
                {data.map((d, i) => {
                    const cx = toX(i);
                    const gH = (d.guests / scaleMaxLeft) * cH, ciH = (d.checkins / scaleMaxLeft) * cH;
                    return (
                        <g key={i}>
                            <rect x={cx - barW - gap / 2} y={pad.top + cH - gH} width={barW} height={gH} rx={3} fill={`${T.accent}CC`} />
                            <rect x={cx + gap / 2} y={pad.top + cH - ciH} width={barW} height={ciH} rx={3} fill={`${T.green}CC`} />
                            <text x={cx - barW / 2 - gap / 2} y={pad.top + cH - gH - 8} textAnchor="middle" fontSize="14" fontWeight="700" fill={T.accent} fontFamily={T.font}>{d.guests}</text>
                            <text x={cx + barW / 2 + gap / 2} y={pad.top + cH - ciH - 8} textAnchor="middle" fontSize="14" fontWeight="700" fill={T.green} fontFamily={T.font}>{d.checkins}</text>
                            <text x={cx} y={h - 6} textAnchor="middle" fontSize="13" fontWeight="700" fill={T.t2} fontFamily={T.font}>{d.hour.replace(":00 ", " ").replace(" PM", " PM").replace(" AM", " AM")}</text>
                        </g>
                    );
                })}
            </svg>
            <div className="rpt-flex-row rpt-gap-16" style={{ justifyContent: "center", marginTop: 4 }}>
                {[
                    { color: T.accent, label: "Guests" },
                    { color: T.green, label: "Check-Ins" },
                ].map((l, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                        <div style={{ width: 10, height: 10, borderRadius: 2, background: l.color }} />
                        <span style={{ fontSize: 11, color: T.t2, fontWeight: 500 }}>{l.label}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

function CompareCards({ items }: any) {
    const isDesktop = useIsDesktop();
    const gap = isDesktop ? 16 : 8;
    const padding = isDesktop ? "20px 16px" : "14px 10px";
    const labelSz = isDesktop ? 11 : 8;
    const valueSz = isDesktop ? 32 : 22;
    const subSz = isDesktop ? 11 : 9;
    return (
        <div style={{ display: "flex", gap }}>
            {items.map((it, i) => (
                <div key={i} style={{ flex: 1, textAlign: "center", padding, background: it.highlight ? it.softBg : T.hover, borderRadius: isDesktop ? 14 : 10, border: `1px solid ${it.highlight ? `${it.accentColor}33` : T.cardBorder}` }}>
                    <div style={{ fontSize: labelSz, color: T.t3, textTransform: "uppercase", letterSpacing: isDesktop ? 0.5 : 0.3 }}>{it.label}</div>
                    <div style={{ fontSize: valueSz, fontWeight: 700, color: it.highlight ? it.accentColor : T.t1, marginTop: isDesktop ? 6 : 4, letterSpacing: isDesktop ? -0.5 : 0 }}>{it.value}</div>
                    <div style={{ fontSize: subSz, color: T.t3, marginTop: isDesktop ? 4 : 2 }}>{it.sub}</div>
                </div>
            ))}
        </div>
    );
}

function CancelByChannelList({ channels }: any) {
    const isDesktop = useIsDesktop();
    const padding = isDesktop ? "12px 14px" : "10px 12px";
    const nameSz = isDesktop ? 13 : 12;
    const rateSz = isDesktop ? 10 : 9;
    const countSz = isDesktop ? 20 : 16;
    return (
        <div style={{ display: "flex", flexDirection: "column", gap: isDesktop ? 10 : 6 }}>
            {channels.map((c, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding, background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 10 }}>
                    <div>
                        <div style={{ fontSize: nameSz, fontWeight: 500, color: T.t1 }}>{c.name}</div>
                        <div style={{ fontSize: rateSz, color: T.t3, marginTop: 2 }}>{isDesktop ? `Rate: ${c.rate}%` : `Cancel rate: ${c.rate}%`}</div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                        <div style={{ fontSize: countSz, fontWeight: 700, color: T.red }}>{c.cancels}</div>
                        {!isDesktop && <div style={{ fontSize: 8, color: T.t3 }}>cancellations</div>}
                    </div>
                </div>
            ))}
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   MOBILE LAYOUT
   ═══════════════════════════════════════════════════════════════════════════ */

function MSummaryKPIs({ items }: any) {
    const singlePill = items.length === 1;
    return (
        <div style={{ display:"flex", justifyContent: singlePill ? "flex-start" : undefined, marginBottom:4, width:"100%" }}>
            <div style={{ display:"grid", gridTemplateColumns:`repeat(${Math.min(items.length,3)},1fr)`, gap:8, width: singlePill ? undefined : "100%", maxWidth: singlePill ? 200 : undefined }}>
                {items.map((it,i) => (
                    <div key={i} style={{ background:T.card, border:`1px solid ${T.cardBorder}`, borderRadius:10, padding:"10px 8px", boxShadow:T.shadow, textAlign:"center" }}>
                        <div style={{ fontSize:7, fontWeight:400, color:T.t3, textTransform:"uppercase", letterSpacing:0.3 }}>{it.label}</div>
                        <div style={{ fontSize:16, fontWeight:700, color:it.color||T.t1, marginTop:3 }}>{it.value}</div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function MMainDashboard({ data, onDrill, isToday, datePeriod, locationId }: any) {
    const heatmap = data?.heatmap || {};
    const { days = [], hours = [], data: heatData = [] } = heatmap;
    const maxHeat = (Array.isArray(heatData) && heatData.length) ? Math.max(...heatData.flat()) : 0;
    const hourLabels = hours.map(h => h>12?`${h-12}P`:h===12?"12P":`${h}A`);
    const allowDrill = !isToday;
    const deltaLabel = getPriorLabel(datePeriod || '');
    return (
        <div style={{ flex:1, overflow:"auto", padding:"8px 16px 16px", display:"flex", flexDirection:"column", gap:8 }}>
                <div style={{ display:"grid", gridTemplateColumns: isToday ? "1fr 1fr" : "1fr 1fr 1fr", gap:8 }}>
                    {(data?.kpis || []).filter(({ key: kKey }) => kKey !== 'seatedEst').map(({ key: kKey, ...kProps }) => (
                        <KPICard key={kKey} id={`checkin-kpi-${kKey}`} {...kProps} deltaLabel={deltaLabel} drillable={allowDrill && MOBILE_DRILLABLE.includes(kKey)} onTap={allowDrill && MOBILE_DRILLABLE.includes(kKey)?()=>onDrill(kKey, `checkin-kpi-${kKey}`):undefined}/>
                    ))}
                </div>
                {isToday && <LiveTodayCheckinTables locationId={locationId} />}
                {!isToday && (
                <>
                <GuestVolumeTrend data={data?.guestVolumeTrend || []} hourlySummary={data?.hourlySummary || []}/>
                <ChartCard id="checkin-section-hourly" title="Hourly Volume" subtitle="Parties per hour" drillable={allowDrill} onTap={allowDrill ? ()=>onDrill("checkins", "checkin-section-hourly") : undefined}>
                    <HourlyBarChart
                        data={(data?.hourly || []).map((r: any) => ({ label: String(r.h ?? ''), value: Number(r.checkins) || 0 }))}
                        barColor={T.accent}
                        chartHeight={80}
                        kpiName="hourly volume"
                    />
                </ChartCard>
                <ChartCard id="checkin-section-partysize" title="Party Sizes" subtitle="Check-ins by group size" drillable={allowDrill} onTap={allowDrill ? ()=>onDrill("avgParty", "checkin-section-partysize") : undefined}>
                    <HourlyBarChart
                        data={(data?.partySize || []).map((r: any) => ({ label: String(r.size ?? ''), value: Number(r.count) || 0 }))}
                        barColor={T.green}
                        chartHeight={70}
                        kpiName="party sizes"
                    />
                </ChartCard>
                <ChartCard title="Wait Time by Party Size" subtitle="Avg wait (min) — red if >30 min" minHeight={0}>
                    {(() => {
                        const wd = getWaitByPartySizeChartData(data);
                        const hasData = Array.isArray(wd) && wd.some((r: any) => Number(r?.w) > 0);
                        return hasData
                            ? <BarChart data={wd} valueKey="w" displayValueKey="waitLabel" labelKey="s" height={70} color={T.orange} threshold={30}/>
                            : <SectionEmpty kpiName="wait time by party size" minHeight={100} />;
                    })()}
                </ChartCard>
                <ChartCard id="checkin-section-dinein" title="Dine-In Duration by Party Size" subtitle="Avg minutes at table" drillable={allowDrill} onTap={allowDrill ? ()=>onDrill("dinein", "checkin-section-dinein") : undefined}>
                    <HourlyBarChart
                        data={(data?.dineinBySize || []).map((d: any) => ({ label: String(d.size ?? ''), value: Number(d.duration) || 0 }))}
                        barColor={T.green}
                        chartHeight={70}
                        kpiName="dine-in duration by party size"
                    />
                </ChartCard>
                {/* Cancellation Trend — commented out
                <ChartCard title="Cancellation Trend" subtitle="Daily cancellations this week" drillable onTap={()=>onDrill("cancels")}>
                    <BarChart data={(data?.cancelTrend || []).map(d=>({d:d.d,v:d.cancels}))} valueKey="v" labelKey="d" height={60} color={T.red}/>
                    <div style={{ display:"flex", justifyContent:"space-between", marginTop:8, padding:"6px 10px", background:T.hover, borderRadius:8 }}>
                        <span style={{ fontSize:10, color:T.t2 }}>Avg Cancel Rate</span>
                        <span style={{ fontSize:10, fontWeight:600, color:T.red }}>{data?.kpis?.find(k=>k.key==="cancels")?.value || "—"}</span>
                    </div>
                </ChartCard>
                */}
                {/* Channel Mix — hidden for all dates
                <ChartCard title="Channel Mix" subtitle="Source of check-ins">
                    <div style={{ display:"flex", height:12, borderRadius:6, overflow:"hidden", marginBottom:10 }}>
                        {(data?.channels || []).map((c,i) => <div key={i} style={{ flex:c.pct, background:c.color }}/>)}
                    </div>
                    <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
                        {(data?.channels || []).map((c,i) => (
                            <div key={i} style={{ display:"flex", alignItems:"center", gap:4 }}>
                                <div style={{ width:6, height:6, borderRadius:2, background:c.color }}/>
                                <span style={{ fontSize:9, color:T.t2 }}>{c.name}</span>
                                <span style={{ fontSize:9, fontWeight:600, color:T.t1 }}>{c.pct}%</span>
                            </div>
                        ))}
                    </div>
                </ChartCard>
                */}
                {/* Check-In Volume Heatmap (mobile) — commented out
                <ChartCard title="Volume Heatmap" subtitle="Hour × day — darker = busier">
                    <div style={{ display:"flex" }}>
                        <div style={{ display:"flex", flexDirection:"column", gap:2, marginRight:4, paddingTop:16 }}>
                            {days.map((d,i) => <div key={i} style={{ height:16, display:"flex", alignItems:"center", fontSize:7, color:T.t3, width:14 }}>{d}</div>)}
                        </div>
                        <div style={{ flex:1 }}>
                            <div style={{ display:"flex", gap:2, marginBottom:2 }}>
                                {hourLabels.map((h,i) => <div key={i} style={{ flex:1, textAlign:"center", fontSize:7, color:T.t3 }}>{h}</div>)}
                            </div>
                            {days.map((_,di) => (
                                <div key={di} style={{ display:"flex", gap:2, marginBottom:2 }}>
                                    {hours.map((_,hi) => {
                                        const v=(heatData[di] && heatData[di][hi]) || 0; const intensity=maxHeat?v/maxHeat:0;
                                        return <div key={hi} style={{ flex:1, height:16, borderRadius:2, background:`rgba(210,58,59,${0.06+intensity*0.59})`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:6, fontWeight:intensity>0.75?600:400, color:intensity>0.75?"#fff":"transparent" }}>{v}</div>;
                                    })}
                                </div>
                            ))}
                        </div>
                    </div>
                </ChartCard>
                */}
                <HourlySummary data={data?.hourlySummary || []}/>
                </>
                )}
        </div>
    );
}

/* Mobile drill-in screens — content only; TopBar is rendered by MobileLayout */
function MDrillCheckins({ data }: any) {
    const checkinsKpi = data?.kpis?.find(k => k.key === "checkins");
    const val = checkinsKpi?.value ?? "0";
    const dailyRows = data?.dailyCheckins || [];
    const isSingleDay = dailyRows.length <= 1;
    return (
        <div style={{ flex:1, overflow:"auto", padding:"8px 16px 16px", display:"flex", flexDirection:"column", gap:8 }}>
                {/* Prior period, Change, Avg/Day commented out — show only this period */}
                <MSummaryKPIs items={[{label:"THIS PERIOD",value:val}]}/>
                {!isSingleDay && (
                <ChartCard title="Daily Check-In Volume" subtitle="Parties per day this week">
                    <HourlyBarChart
                        data={dailyRows.map((r: any) => ({ label: String(r.d ?? ''), value: Number(r.checkins) || 0 }))}
                        barColor={T.accent}
                        chartHeight={80}
                        kpiName="daily check-in volume"
                    />
                </ChartCard>
                )}
                <ChartCard title="By Hour of Day" subtitle="When walk-ins arrive">
                    <HourlyBarChart
                        data={(data?.hourly || []).map((r: any) => ({ label: String(r.h ?? ''), value: Number(r.checkins) || 0 }))}
                        barColor={T.accent}
                        chartHeight={100}
                        kpiName="by hour of day"
                    />
                </ChartCard>
                {/* By Channel — hidden for all dates
                <ChartCard title="By Channel" subtitle="Check-in source breakdown">
                    <ProgressList data={(data?.channels || []).map(c=>({name:c.name,count:c.count}))} labelKey="name" valueKey="count" color={T.accent}/>
                </ChartCard>
                */}
        </div>
    );
}

function MDrillGuests({ data }: any) {
    const guestsKpi = data?.kpis?.find(k => k.key === "guests");
    const val = guestsKpi?.value ?? "0";
    const dailyRows = data?.dailyCheckins || [];
    const isSingleDay = dailyRows.length <= 1;
    return (
        <div style={{ flex:1, overflow:"auto", padding:"8px 16px 16px", display:"flex", flexDirection:"column", gap:8 }}>
                {/* Prior period, Avg/Day commented out — show only this period */}
                <MSummaryKPIs items={[{label:"THIS PERIOD",value:val}]}/>
                <GuestVolumeTrend data={data?.guestVolumeTrend || []} hourlySummary={data?.hourlySummary || []} compact={true}/>
                {!isSingleDay && (
                <ChartCard title="Daily Guest Volume" subtitle="Total guests per day">
                    <BarChart data={dailyRows} valueKey="guests" labelKey="d" height={80} color={T.green}/>
                </ChartCard>
                )}
        </div>
    );
}

function MDrillAvgParty({ data }: any) {
    const partySizeData = data?.partySize || [];
    const total = partySizeData.reduce((s,d)=>s+d.count,0);
    const avgPartyKpi = data?.kpis?.find(k => k.key === "avgParty");
    const mostCommon = partySizeData.length ? partySizeData.reduce((a,b) => (b.count > a.count ? b : a), partySizeData[0]) : null;
    return (
        <div style={{ flex:1, overflow:"auto", padding:"8px 16px 16px", display:"flex", flexDirection:"column", gap:8 }}>
                <MSummaryKPIs items={[{label:"AVG SIZE",value:avgPartyKpi?.value ?? "—"},{label:"MOST COMMON",value:mostCommon ? `Party of ${mostCommon.size}` : "—"},{label:"TOTAL PARTIES",value:total}]}/>
                <ChartCard title="Party Size Distribution" subtitle="Count and % share by group size">
                    <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                        {partySizeData.map((d,i) => { const pct=total ? ((d.count/total)*100).toFixed(0) : 0; return (
                            <div key={i}>
                                <div style={{ display:"flex", justifyContent:"space-between", marginBottom:3 }}>
                                    <span style={{ fontSize:11, color:T.t2 }}>Party of {d.size}</span>
                                    <span style={{ fontSize:11, color:T.t1 }}><span style={{ fontWeight:600 }}>{d.count}</span> <span style={{ color:T.t3 }}>({pct}%)</span></span>
                                </div>
                                <div style={{ height:5, background:T.hover, borderRadius:3, overflow:"hidden" }}>
                                    <div style={{ height:"100%", width:`${pct}%`, borderRadius:3, background:`${T.green}AA` }}/>
                                </div>
                            </div>
                        ); })}
                    </div>
                </ChartCard>
                <ChartCard title="Wait Time by Party Size" subtitle="Larger parties wait longer">
                    <BarChart data={getWaitByPartySizeChartData(data)} valueKey="w" displayValueKey="waitLabel" labelKey="s" height={70} color={T.orange} threshold={30}/>
                    <div style={{ display:"flex", alignItems:"center", gap:4, marginTop:8, justifyContent:"flex-end" }}>
                        <div style={{ width:10, height:3, background:T.accent, borderRadius:1 }}/><span style={{ fontSize:8, color:T.accent }}>Over 30 min</span>
                    </div>
                </ChartCard>
                <ChartCard title="Dine-In Duration by Party Size" subtitle="Avg minutes at table">
                    <HourlyBarChart
                        data={(data?.dineinBySize || []).map((d: any) => ({ label: String(d.size ?? ''), value: Number(d.duration) || 0 }))}
                        barColor={T.green}
                        chartHeight={70}
                        kpiName="dine-in duration by party size"
                    />
                </ChartCard>
        </div>
    );
}

function MDrillCancels({ data }: any) {
    const cancelKpi = data?.kpis?.find(k => k.key === "cancels");
    const totalCancelKpi = data?.kpis?.find(k => k.key === "totalCancel");
    return (
        <div style={{ flex:1, overflow:"auto", padding:"8px 16px 16px", display:"flex", flexDirection:"column", gap:8 }}>
                {/* Keep only Total and Rate — rest commented out */}
                <MSummaryKPIs items={[{label:"TOTAL CANCELLATIONS",value:totalCancelKpi?.value ?? "0",color:T.red},{label:"CANCEL RATE",value:cancelKpi?.value ?? "—"}]}/>
                {/* Daily Cancellation Trend, By Hour, By Channel — commented out
                <ChartCard title="Daily Cancellation Trend" subtitle="Cancellations per day with rate">
                    <BarChart data={(data?.cancelTrend || []).map(d=>({d:d.d,v:d.cancels}))} valueKey="v" labelKey="d" height={70} color={T.red}/>
                    <div style={{ display:"flex", gap:4, marginTop:6 }}>
                        {(data?.cancelTrend || []).map((d,i)=><div key={i} style={{ flex:1, textAlign:"center", fontSize:7, color:T.t3 }}>{d.rate}%</div>)}
                    </div>
                    <div style={{ textAlign:"center", fontSize:7, color:T.t3, marginTop:2 }}>daily cancel rate %</div>
                </ChartCard>
                <ChartCard title="Cancellations by Hour" subtitle="When do walk-aways happen?">
                    <BarChart data={data?.cancelByHour || []} valueKey="c" labelKey="h" height={60} color={T.red}/>
                </ChartCard>
                <ChartCard title="Cancellations by Channel" subtitle="Which channels lose the most?">
                    <CancelByChannelList channels={data?.cancelByChannel || []}/>
                </ChartCard>
                */}
        </div>
    );
}

function MDrillWaitTime({ data }: any) {
    const waitKpi = data?.kpis?.find(k => k.key === "waitTime");
    return (
        <div style={{ flex:1, overflow:"auto", padding:"8px 16px 16px", display:"flex", flexDirection:"column", gap:8 }}>
                {/* Shortest Day, Longest Day commented out */}
                <MSummaryKPIs items={[{label:"AVG WAIT",value:waitKpi?.value ?? "0 min"}]}/>
                {/* Wait Time by Day of Week — commented out
                <ChartCard title="Wait Time by Day of Week" subtitle="Avg minutes from check-in to seating">
                    <BarChart data={data?.waitByDay || []} valueKey="wait" labelKey="d" height={80} color={T.orange} threshold={25}/>
                    <div style={{ display:"flex", alignItems:"center", gap:4, marginTop:8, justifyContent:"flex-end" }}>
                        <div style={{ width:10, height:3, background:T.accent, borderRadius:1 }}/><span style={{ fontSize:8, color:T.accent }}>Over 25 min</span>
                    </div>
                </ChartCard>
                */}
                <ChartCard title="Wait Time by Party Size" subtitle="Larger groups wait longer">
                    <ProgressList data={(data?.partySize || []).map(d=>({label:`Party of ${d.size}`,wait:d.wait}))} labelKey="label" valueKey="wait" valueSuffix=" min" color={T.orange}/>
                </ChartCard>
                {/* Weekday / Weekend compare cards — commented out
                <ChartCard title="Weekend vs Weekday" subtitle="Average wait comparison">
                    <CompareCards items={[
                        {label:"Weekday",value:"—",sub:"Mon–Fri avg",highlight:false},
                        {label:"Weekend",value:"—",sub:"Sat–Sun avg",highlight:true,softBg:T.orangeSoft,accentColor:T.orange},
                    ]}/>
                </ChartCard>
                */}
        </div>
    );
}

function MDrillDineIn({ data }: any) {
    // const dineInKpi = data?.kpis?.find((k: any) => k.key === "dinein");
    const dineinRows = (data?.dineinBySize || []).map((d: any) => ({ label: `Party of ${d.size}`, dur: d.duration }));
    const hasData = dineinRows.length > 0;
    return (
        <div style={{ flex:1, overflow:"auto", padding:"8px 16px 16px", display:"flex", flexDirection:"column", gap:8 }}>
            {/* <MSummaryKPIs items={[{ label: "AVG DINE-IN DURATION", value: dineInKpi?.value ?? "—" }]} /> */}
            <ChartCard title="Duration by Party Size" subtitle="Avg minutes at table per group size">
                {hasData
                    ? <ProgressList data={dineinRows} labelKey="label" valueKey="dur" valueSuffix=" min" color={T.green} />
                    : <SectionEmpty kpiName="dine-in duration by party size" minHeight={120} />
                }
            </ChartCard>
        </div>
    );
}

function MobileLayout({ data, loading, error, refetch, datePeriod, setDatePeriod, isToday, locationId }: any) {
    const [screen, setScreen] = useState("main");
    const scrollTargetRef = useRef<string | null>(null);
    const handlePtrRefresh = useCallback(() => {
        refetch?.();
    }, [refetch]);
    const { scrollRef: ptrScrollRef, pullDistance, isRefreshing: ptrRefreshing } = usePullToRefresh({ onRefresh: handlePtrRefresh });
    const handleDrill = useCallback((key: string, sourceId?: string) => {
        scrollTargetRef.current = sourceId || `checkin-kpi-${key}`;
        setScreen(key);
    }, []);
    const setTopBarProps = useDesktopTopBar();
    const drillMap = {
        checkins: <MDrillCheckins data={data} />,
        guests:   <MDrillGuests   data={data} />,
        avgParty: <MDrillAvgParty data={data} />,
        cancels:  <MDrillCancels  data={data} />,
        waitTime: <MDrillWaitTime data={data} />,
        dinein:   <MDrillDineIn   data={data} />,
    };
    const isDrill = screen !== "main" && M_DRILL_HEADERS[screen];
    useDrillHistoryBack(!!isDrill, () => setScreen("main"));
    useSwipeBack();

    useEffect(() => {
        if (screen !== "main") {
            scrollPageToTop();
            return;
        }
        if (scrollTargetRef.current) {
            const targetId = scrollTargetRef.current;
            scrollTargetRef.current = null;
            const timer = setTimeout(() => {
                scrollToTarget(targetId);
            }, 400);
            return () => clearTimeout(timer);
        }
    }, [screen]);

    useEffect(() => {
        setTopBarProps({
            title: isDrill ? M_DRILL_HEADERS[screen].title : "Check-In Analytics",
            dateFilters: isDrill ? undefined : { value: datePeriod, onChange: setDatePeriod },
            onBack: isDrill ? () => setScreen("main") : undefined,
        });
    }, [setTopBarProps, screen, isDrill, datePeriod, setDatePeriod]);

    return (
        <div
            className="checkin-report-root"
            style={{
                ["--checkin-bg" as any]: T.bg,
                ["--checkin-card" as any]: T.card,
                ["--checkin-shadow" as any]: T.shadow,
                ["--checkin-card-shadow" as any]: T.cardShadow,
                ["--checkin-font" as any]: T.font,
                width:"100%",
                minHeight:"100vh",
            }}
        >
            <PullToRefreshIndicator pullDistance={pullDistance} isRefreshing={ptrRefreshing} />
            <TopBar variant="mobile" />
            <div ref={ptrScrollRef} style={{ flex: 1, overflowY: 'auto' }}>
                {!locationId ? (
                    <div style={{ padding: 24, textAlign: "center", color: T.t3, fontFamily: T.font }}>Select a location to view Check-In report.</div>
                ) : error ? (
                    <SectionError kpiName="check-in analytics" onRetry={refetch} size="medium" minHeight={320} />
                ) : loading ? (
                    <CheckinMobileShimmer isToday={isToday} />
                ) : (
                    screen === "main" ? <MMainDashboard data={data} onDrill={handleDrill} isToday={isToday} datePeriod={datePeriod} locationId={locationId}/> : drillMap[screen] || <MMainDashboard data={data} onDrill={handleDrill} isToday={isToday} datePeriod={datePeriod} locationId={locationId}/>
                )}
            </div>
            <BottomNav />
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   DESKTOP LAYOUT
   ═══════════════════════════════════════════════════════════════════════════ */
function DSummaryStrip({ items }: any) {
    const singlePill = items.length === 1;
    return (
        <div className="checkin-summary-strip" style={{ justifyContent: singlePill ? "flex-start" : undefined }}>
            {items.map((it,i) => (
                <div key={i} className="checkin-summary-strip__item" style={{ flex: singlePill ? "0 1 auto" : 1, maxWidth: singlePill ? 340 : undefined, border:`1px solid ${T.cardBorder}` }}>
                    <div style={{ fontSize:11, fontWeight:500, color:T.t3, textTransform:"uppercase", letterSpacing:0.5 }}>{it.label}</div>
                    <div style={{ fontSize:24, fontWeight:700, color:it.color||T.t1, marginTop:6, letterSpacing:-0.5 }}>{it.value}</div>
                </div>
            ))}
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   LIVE / TODAY CHECK-IN TABLES (visible only for the "Today" date filter)
   Two tabs: Live Check-ins (type=livecheckin) and Today Check-ins (type=todaycheckin)
   Both APIs are fetched on mount. Switching tabs also triggers a fresh fetch
   for the selected tab so status changes are reflected immediately.
   ═══════════════════════════════════════════════════════════════════════════ */
type CheckinTabState = {
    page: number;
    pageSize: number;
    search: string;
    debouncedSearch: string;
    content: any[];
    totalElements: number;
    totalPages: number;
    loading: boolean;
    error: boolean;
};
const INITIAL_TAB_STATE: CheckinTabState = {
    page: 0, pageSize: 10, search: "", debouncedSearch: "",
    content: [], totalElements: 0, totalPages: 0, loading: true, error: false,
};

function LiveTodayCheckinTables({ locationId }: any) {
    const isDesktop = useIsDesktop();
    const [activeTab, setActiveTab] = useState<"live" | "today">("live");
    const [phoneVisible, setPhoneVisible] = useState(false);
    const [sortBy, setSortBy] = useState("checkInTime");
    const [sortAsc, setSortAsc] = useState(true);

    const [liveState, setLiveState] = useState<CheckinTabState>(INITIAL_TAB_STATE);
    const [todayState, setTodayState] = useState<CheckinTabState>(INITIAL_TAB_STATE);

    /* Debounce search per tab — reset page to 0 on search change in same update */
    useEffect(() => {
        const t = setTimeout(() => setLiveState((s) => (s.search === s.debouncedSearch ? s : { ...s, debouncedSearch: s.search, page: 0 })), 350);
        return () => clearTimeout(t);
    }, [liveState.search]);
    useEffect(() => {
        const t = setTimeout(() => setTodayState((s) => (s.search === s.debouncedSearch ? s : { ...s, debouncedSearch: s.search, page: 0 })), 350);
        return () => clearTimeout(t);
    }, [todayState.search]);

    /* Retry tick — increments to force a re-fetch of the corresponding tab */
    const [liveRetryTick, setLiveRetryTick] = useState(0);
    const [todayRetryTick, setTodayRetryTick] = useState(0);

    /* Fetch Live tab — runs on mount, and when page/pageSize/debouncedSearch change */
    useEffect(() => {
        if (!locationId) { setLiveState((s) => ({ ...s, loading: false, content: [], totalElements: 0, totalPages: 0, error: false })); return; }
        let cancelled = false;
        setLiveState((s) => ({ ...s, loading: true, error: false }));
        const opts: any = { locationId, page: liveState.page + 1, size: liveState.pageSize };
        const sv = (liveState.debouncedSearch || "").trim();
        if (sv) opts.search = sv;
        opts.sortBy = sortBy;
        opts.sortAsc = sortAsc;
        getLiveCheckInTable(opts)
            .then((res: any) => {
                if (cancelled) return;
                const content = Array.isArray(res?.content) ? res.content : [];
                setLiveState((s) => ({ ...s, loading: false, content, totalElements: Number(res?.totalElements) || 0, totalPages: Number(res?.totalPages) || 0, error: false }));
            })
            .catch(() => { if (!cancelled) setLiveState((s) => ({ ...s, loading: false, content: [], totalElements: 0, totalPages: 0, error: true })); });
        return () => { cancelled = true; };
    }, [locationId, liveState.page, liveState.pageSize, liveState.debouncedSearch, liveRetryTick, sortBy, sortAsc]);

    /* Fetch Today tab — runs on mount, and when page/pageSize/debouncedSearch change */
    useEffect(() => {
        if (!locationId) { setTodayState((s) => ({ ...s, loading: false, content: [], totalElements: 0, totalPages: 0, error: false })); return; }
        let cancelled = false;
        setTodayState((s) => ({ ...s, loading: true, error: false }));
        const opts: any = { locationId, page: todayState.page + 1, size: todayState.pageSize };
        const sv = (todayState.debouncedSearch || "").trim();
        if (sv) opts.search = sv;
        opts.sortBy = sortBy;
        opts.sortAsc = sortAsc;
        getLiveCheckInToday(opts)
            .then((res: any) => {
                if (cancelled) return;
                const content = Array.isArray(res?.content) ? res.content : [];
                setTodayState((s) => ({ ...s, loading: false, content, totalElements: Number(res?.totalElements) || 0, totalPages: Number(res?.totalPages) || 0, error: false }));
            })
            .catch(() => { if (!cancelled) setTodayState((s) => ({ ...s, loading: false, content: [], totalElements: 0, totalPages: 0, error: true })); });
        return () => { cancelled = true; };
    }, [locationId, todayState.page, todayState.pageSize, todayState.debouncedSearch, todayRetryTick, sortBy, sortAsc]);

    const retryActiveTab = () => {
        if (activeTab === "live") setLiveRetryTick((n) => n + 1);
        else setTodayRetryTick((n) => n + 1);
    };
    const handleTabChange = (tab: "live" | "today") => {
        if (tab === activeTab) return;
        setActiveTab(tab);
        if (tab === "live") setLiveRetryTick((n) => n + 1);
        else setTodayRetryTick((n) => n + 1);
    };

    /* Active tab accessors */
    const activeState = activeTab === "live" ? liveState : todayState;
    const setActiveState = activeTab === "live" ? setLiveState : setTodayState;
    const { page, pageSize, search, content, totalElements, totalPages, loading } = activeState;
    const setPage = (updater: (p: number) => number | number) => setActiveState((s) => ({ ...s, page: typeof updater === "function" ? (updater as any)(s.page) : updater }));
    const setPageSize = (n: number) => setActiveState((s) => ({ ...s, pageSize: n, page: 0 }));
    const setSearch = (v: string) => setActiveState((s) => ({ ...s, search: v }));

    const maskPhone = (p: any) => {
        const s = String(p ?? "").replace(/[^\d]/g, "");
        if (!s) return "—";
        if (s.length < 4) return "*".repeat(s.length);
        return `(***) ***-${s.slice(-4)}`;
    };

    const getStatusPillConfig = (status: any) => {
        return getStatusPill(status);
    };

    /**
     * Case-insensitive lookup: walk all top-level keys of `row` and return the first
     * value whose key matches any name in `names`. Handles arbitrary API casings
     * (status / Status / STATUS / checkinStatus / checkin_status / etc.).
     */
    const pickField = (row: any, names: string[]): any => {
        if (!row || typeof row !== "object") return "";
        const wanted = names.map((n) => String(n).toLowerCase().replace(/[_\s-]/g, ""));
        for (const key of Object.keys(row)) {
            const norm = String(key).toLowerCase().replace(/[_\s-]/g, "");
            if (wanted.includes(norm) && row[key] != null && row[key] !== "") return row[key];
        }
        return "";
    };
    const resolveStatus = (row: any) =>
        activeTab === "live"
            ? pickField(row, ["liveCheckInStatus", "status", "checkInStatus", "checkinStatus", "currentStatus", "statusName", "orderStatus", "state"])
            : pickField(row, ["todayCheckInStatus", "status", "checkInStatus", "checkinStatus", "currentStatus", "statusName", "orderStatus", "state"]);

    const tabs: Array<{ key: "live" | "today"; label: string }> = [
        { key: "live", label: "Live Check-ins" },
        { key: "today", label: "Today Check-ins" },
    ];
    const handleSort = useCallback((col: string) => {
        if (sortBy === col) setSortAsc(a => !a);
        else { setSortBy(col); setSortAsc(true); }
    }, [sortBy]);

    const activeTitle = activeTab === "live" ? "Live Check-ins" : "Today Check-ins";
    const gridCols = "minmax(80px,0.7fr) minmax(110px,1fr) minmax(120px,1fr) minmax(70px,0.65fr) minmax(110px,0.95fr) minmax(110px,0.95fr) minmax(70px,0.6fr) minmax(80px,0.65fr) minmax(100px,0.9fr) minmax(100px,0.9fr)";
    const headers = [
        { key: "checkInNumber", label: "Check-in", sortable: true },
        { key: "guestName", label: "Guest name", sortable: true },
        { key: "phone", label: "Phone", sortable: false },
        { key: "guestSize", label: "Guest size", sortable: true },
        { key: "checkInTime", label: "Check-in time", sortable: true },
        { key: "assignedTime", label: "Assigned time", sortable: true },
        { key: "tableName", label: "Table", sortable: true },
        { key: "waitTime", label: "Wait time", sortable: false },
        { key: "status", label: "Status", sortable: true },
        { key: "channel", label: "Channel", sortable: true },
    ];

    const displayRows = content || [];
    const start = totalElements === 0 ? 0 : page * pageSize + 1;
    const end = totalElements === 0 ? 0 : Math.min((page + 1) * pageSize, totalElements);

    return (
        <div className="rpt-card" style={{ borderRadius: 14, padding: isDesktop ? "20px 20px 20px 20px" : 14, overflow: "hidden", background: T.card, border: `1px solid ${T.cardBorder}`, boxShadow: T.cardShadow }}>
            {/* Tabs */}
            <div style={{ display: "flex", gap: 4, borderBottom: `1px solid ${T.divider}`, marginBottom: 16, overflowX: "auto" }}>
                {tabs.map((t) => {
                    const isActive = activeTab === t.key;
                    return (
                        <button
                            key={t.key}
                            type="button"
                            onClick={() => handleTabChange(t.key)}
                            style={{
                                border: "none", background: "transparent", cursor: "pointer",
                                padding: isDesktop ? "10px 16px" : "8px 12px",
                                fontSize: isDesktop ? 13 : 12,
                                fontWeight: isActive ? 600 : 500,
                                color: isActive ? T.t1 : T.t2,
                                borderBottom: `2px solid ${isActive ? T.accent : "transparent"}`,
                                fontFamily: T.font,
                                whiteSpace: "nowrap",
                            }}
                        >
                            {t.label}
                        </button>
                    );
                })}
            </div>

            {/* Title + search */}
            <div className={`checkin-live-table__header ${isDesktop ? "checkin-live-table__header--desktop" : "checkin-live-table__header--mobile"}`}>
                <div className="checkin-live-table__title-row">
                    <div className="checkin-live-table__title">{activeTitle}</div>
                    <span className="resv-table__count-badge">{totalElements}</span>
                </div>
                {isDesktop && (
                    <div className="rpt-flex-row rpt-flex-center resv-table__search-wrap checkin-live-table__search-wrap-desktop">
                        <span className="material-symbols-rounded resv-table__search-icon">search</span>
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search by check-in number, table number, customer name"
                            className="resv-table__search-input checkin-live-table__search-input--desktop"
                        />
                    </div>
                )}
            </div>
            {!isDesktop && (
                <div className="checkin-live-table__search-row">
                    <div className="rpt-flex-row rpt-flex-center resv-table__search-wrap">
                        <span className="material-symbols-rounded resv-table__search-icon">search</span>
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search..."
                            className="resv-table__search-input resv-table__search-input--mobile"
                        />
                    </div>
                </div>
            )}

            {/* Table — when empty/errored, render the status card at viewport width
                (outside the minWidth:960 horizontal-scroll wrapper) so the message
                stays centred on narrow screens. */}
            {!loading && activeState.error ? (
                <SectionError
                    kpiName={activeTab === "live" ? "live check-ins" : "today check-ins"}
                    onRetry={retryActiveTab}
                    size="medium"
                    minHeight={240}
                />
            ) : !loading && content.length === 0 ? (
                <SectionEmpty
                    kpiName={activeTab === "live" ? "live check-ins" : "today check-ins"}
                    size="medium"
                    minHeight={220}
                />
            ) : (
            <div style={{ overflowX: "auto" }}>
                <div style={{ minWidth: 1060 }}>
                    <div style={{ display: "grid", gridTemplateColumns: gridCols, columnGap: 12, padding: "10px 4px", borderBottom: `1px solid ${T.divider}` }}>
                        {headers.map((h) => (
                            <div key={h.key} onClick={h.sortable ? () => handleSort(h.key) : undefined} style={{ fontSize: 11, fontWeight: 600, color: T.t3, textTransform: "uppercase", letterSpacing: 0.3, cursor: h.sortable ? "pointer" : "default", userSelect: "none" }}>
                                {h.label}
                                {h.key === "phone" && (
                                    <span
                                        onClick={() => setPhoneVisible((v) => !v)}
                                        className="material-symbols-rounded"
                                        style={{ fontSize: 14, color: T.t3, marginLeft: 4, cursor: "pointer", verticalAlign: "middle" }}
                                    >
                                        {phoneVisible ? "visibility" : "visibility_off"}
                                    </span>
                                )}
                                {h.sortable && (
                                    <span style={{ marginLeft: 4, fontSize: 11, color: sortBy === h.key ? T.accent : T.t3, opacity: sortBy === h.key ? 1 : 0.4 }}>
                                        {sortBy === h.key ? (sortAsc ? "↑" : "↓") : "⇅"}
                                    </span>
                                )}
                            </div>
                        ))}
                    </div>
                    {loading ? (
                        <>
                            {Array.from({ length: 5 }).map((_, i) => (
                                <div key={i} style={{ display: "grid", gridTemplateColumns: gridCols, columnGap: 12, padding: "12px 4px", borderBottom: `1px solid ${T.divider}` }}>
                                    {headers.map((h) => (<Bone key={h.key} h={14} r={4} />))}
                                </div>
                            ))}
                        </>
                    ) : (
                        displayRows.map((row: any, ri: number) => {
                            const cn = row?.checkInNumber ?? row?.orderNumber ?? row?.id ?? row?.checkinNumber ?? "";
                            const name = row?.customerName ?? row?.guestName ?? row?.name ?? "";
                            const phone = row?.phoneNumber ?? row?.phone ?? "";
                            const size = row?.partySize ?? row?.guestSize ?? row?.guestCount ?? "";
                            const checkInTime = row?.checkInTime ?? row?.checkinTime ?? row?.time ?? "";
                            const assignedTime = row?.assignedTime ?? row?.assignTime ?? row?.seatTime ?? "";
                            const table = row?.tableName ?? row?.table ?? row?.tableNumber ?? "";
                            const waitTime = row?.waitTime ?? row?.wait ?? null;
                            const status = resolveStatus(row);
                            const channel = row?.source ?? row?.channel ?? row?.channelName ?? row?.checkinChannel ?? "";
                            const pill = getStatusPillConfig(status);
                            return (
                                <div key={ri} style={{ display: "grid", gridTemplateColumns: gridCols, columnGap: 12, padding: "12px 4px", borderBottom: `1px solid ${T.divider}`, alignItems: "center" }}>
                                    <div style={{ fontSize: 12, color: T.t1, fontWeight: 500 }}>#{cn || "—"}</div>
                                    <div style={{ fontSize: 12, color: T.t1 }}>{name || "—"}</div>
                                    <div style={{ fontSize: 12, color: T.t1 }}>{phoneVisible ? (phone || "—") : maskPhone(phone)}</div>
                                    <div style={{ fontSize: 12, color: T.t1 }}>{size !== "" && size != null ? size : "—"}</div>
                                    <div style={{ fontSize: 12, color: T.t1 }}>{checkInTime || "—"}</div>
                                    <div style={{ fontSize: 12, color: T.t1 }}>{assignedTime || "—"}</div>
                                    <div title={table || "—"} style={{ fontSize: 12, color: T.t1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{table || "—"}</div>
                                    <div style={{ fontSize: 12, color: T.t1 }}>{waitTime != null && Number(String(waitTime).replace(/[^\d.]/g, "") || "0") !== 0 ? waitTime : "—"}</div>
                                    <div>
                                        <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: 12, fontSize: 11, fontWeight: 500, background: pill.bg, color: pill.color }}>
                                            {pill.label}
                                        </span>
                                    </div>
                                    <div style={{ fontSize: 12, color: T.t1 }}>{channel || "—"}</div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>
            )}

            {/* Pagination */}
            {totalElements > 0 && (
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12, flexWrap: "wrap", gap: 8 }}>
                    {isDesktop ? (
                        <>
                            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: T.t2 }}>
                                <span>Result per page:</span>
                                <select
                                    value={pageSize}
                                    onChange={(e) => setPageSize(Number(e.target.value))}
                                    style={{ border: `1px solid ${T.cardBorder}`, borderRadius: 6, padding: "4px 6px", fontSize: 12, color: T.t1, background: T.surface, fontFamily: T.font, cursor: "pointer" }}
                                >
                                    {[10, 20, 50].map((n) => <option key={n} value={n}>{n}</option>)}
                                </select>
                                <span>{start}-{end} of {totalElements}</span>
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                <button
                                    type="button"
                                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                                    disabled={page === 0}
                                    style={{ border: `1px solid ${T.cardBorder}`, background: T.surface, borderRadius: 6, padding: "4px 10px", fontSize: 12, color: page === 0 ? T.t3 : T.t2, cursor: page === 0 ? "not-allowed" : "pointer", fontFamily: T.font }}
                                >
                                    ← Prev
                                </button>
                                <div style={{ minWidth: 28, height: 28, borderRadius: 6, border: `1px solid ${T.cardBorder}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, color: T.t1, background: T.surface, padding: "0 8px" }}>
                                    {page + 1}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setPage((p) => (totalPages ? Math.min(totalPages - 1, p + 1) : p + 1))}
                                    disabled={totalPages ? page >= totalPages - 1 : false}
                                    style={{ border: `1px solid ${T.cardBorder}`, background: T.surface, borderRadius: 6, padding: "4px 10px", fontSize: 12, color: (totalPages && page >= totalPages - 1) ? T.t3 : T.t2, cursor: (totalPages && page >= totalPages - 1) ? "not-allowed" : "pointer", fontFamily: T.font }}
                                >
                                    Next →
                                </button>
                            </div>
                        </>
                    ) : (
                        <>
                            <span style={{ fontSize: 12, color: T.t3 }}>{start}-{end} of {totalElements}</span>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <select
                                    value={pageSize}
                                    onChange={(e) => setPageSize(Number(e.target.value))}
                                    style={{ border: `1px solid ${T.cardBorder}`, borderRadius: 10, padding: "5px 10px", fontSize: 12, color: T.t1, background: T.surface, fontFamily: T.font, cursor: "pointer" }}
                                >
                                    {[10, 20, 50].map((n) => <option key={n} value={n}>{n} / page</option>)}
                                </select>
                                <button
                                    type="button"
                                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                                    disabled={page === 0}
                                    style={{ border: "none", background: "transparent", padding: 2, cursor: page === 0 ? "not-allowed" : "pointer", opacity: page === 0 ? 0.35 : 1 }}
                                >
                                    <span className="material-symbols-rounded" style={{ fontSize: 18, color: T.t2 }}>chevron_left</span>
                                </button>
                                <span style={{ fontSize: 12, color: T.t2 }}>{page + 1} / {totalPages || 1}</span>
                                <button
                                    type="button"
                                    onClick={() => setPage((p) => (totalPages ? Math.min(totalPages - 1, p + 1) : p + 1))}
                                    disabled={totalPages ? page >= totalPages - 1 : false}
                                    style={{ border: "none", background: "transparent", padding: 2, cursor: (totalPages && page >= totalPages - 1) ? "not-allowed" : "pointer", opacity: (totalPages && page >= totalPages - 1) ? 0.35 : 1 }}
                                >
                                    <span className="material-symbols-rounded" style={{ fontSize: 18, color: T.t2 }}>chevron_right</span>
                                </button>
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

function DMainContent({ data, onDrill, isToday, datePeriod, locationId }: any) {
    const heatmap = data?.heatmap || {};
    const { dFull: days = [], hFull: hours = [], dataFull: heat = [] } = heatmap;
    const maxHeat  = (Array.isArray(heat) && heat.length) ? Math.max(...heat.flat()) : 0;
    const hourLabels = hours.map(h => h>12?`${h-12}P`:h===12?"12P":`${h}A`);
    const chData   = data?.channels || [];
    const chTotal  = chData.reduce((s,c)=>s+c.value,0);
    const circ     = 2*Math.PI*58;
    let offset     = 0;
    const allowDrill = !isToday;
    const hourlyVolumeData = data?.hourly || [];
    const deltaLabel = getPriorLabel(datePeriod || '');
    const kpiGridCols = isToday ? "repeat(3, minmax(0, 1fr))" : "repeat(auto-fit, minmax(180px, 1fr))";

    return (
            <div className="rpt-page">
            <div style={{ display:"grid", gap:16, gridTemplateColumns: kpiGridCols }}>
                {(data?.kpis || []).filter(({ key: kKey }) => kKey !== 'seatedEst').map(({ key: kKey, ...kProps }) => <KPICard key={kKey} id={`checkin-kpi-${kKey}`} {...kProps} deltaLabel={deltaLabel} drillable={allowDrill && DRILLABLE.includes(kKey)} onTap={allowDrill && DRILLABLE.includes(kKey)?()=>onDrill(kKey, `checkin-kpi-${kKey}`):undefined}/>)}
            </div>
            {isToday && <LiveTodayCheckinTables locationId={locationId} />}
            {!isToday && (
            <>
            <div className="rpt-flex-row rpt-gap-16" style={{ alignItems: "stretch" }}>
                <GuestVolumeTrend data={data?.guestVolumeTrend || []} hourlySummary={data?.hourlySummary || []} height={360}/>
                <div style={{ flex: 1 }}>
                    <HourlySummary data={data?.hourlySummary || []}/>
                </div>
            </div>
            <div className="rpt-flex-row rpt-gap-16">
                <ChartCard id="checkin-section-hourly" title="Hourly Check-In Volume" subtitle="Parties checked in per hour — click to drill" flex={2} drillable={allowDrill} onTap={allowDrill ? ()=>onDrill("checkins", "checkin-section-hourly") : undefined}>
                    <HourlyBarChart
                        data={hourlyVolumeData.map((r: any) => ({ label: String(r.h ?? ''), value: Number(r.checkins) || 0 }))}
                        barColor={T.accent}
                        chartHeight={160}
                        kpiName="hourly check-in volume"
                    />
                </ChartCard>
                {/* Channel Mix — hidden for all dates
                <ChartCard title="Channel Mix" subtitle="Check-in source distribution" flex={1}>
                    <div style={{ display:"flex", alignItems:"center", gap:24 }}>
                        <svg width={140} height={140} viewBox="0 0 140 140" style={{ flexShrink:0 }}>
                            {chData.map((c,i) => { const pct=c.value/chTotal; const dash=circ*pct; const gap=circ-dash; const o=offset; offset+=dash; return <circle key={i} cx={70} cy={70} r={58} fill="none" stroke={c.color} strokeWidth={16} strokeDasharray={`${dash} ${gap}`} strokeDashoffset={-o} transform="rotate(-90 70 70)"/>; })}
                            <text x={70} y={66} textAnchor="middle" fontSize="18" fontWeight="700" fill={T.t1} fontFamily={T.font}>{chTotal}</text>
                            <text x={70} y={82} textAnchor="middle" fontSize="9"  fill={T.t2} fontFamily={T.font}>Parties</text>
                        </svg>
                        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                            {chData.map((c,i) => (
                                <div key={i} style={{ display:"flex", alignItems:"center", gap:8 }}>
                                    <div style={{ width:8, height:8, borderRadius:2, background:c.color, flexShrink:0 }}/>
                                    <span style={{ fontSize:11, color:T.t2, width:72 }}>{c.name}</span>
                                    <span style={{ fontSize:11, fontWeight:600, color:T.t1 }}>{c.pct}%</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </ChartCard>
                */}
            </div>
            <div className="rpt-flex-row rpt-gap-16">
                <ChartCard id="checkin-section-partysize" title="Party Size Distribution" subtitle="Check-ins by group size — click to drill" flex={1} drillable={allowDrill} onTap={allowDrill ? ()=>onDrill("avgParty", "checkin-section-partysize") : undefined}>
                    <HourlyBarChart
                        data={(data?.partySize || []).map((r: any) => ({ label: String(r.size ?? ''), value: Number(r.count) || 0 }))}
                        barColor={T.green}
                        chartHeight={140}
                        kpiName="party size distribution"
                    />
                </ChartCard>
                <ChartCard title="Wait Time by Party Size" subtitle="Avg wait (min) — red if >30 min" flex={1} minHeight={0} alignSelf="flex-start">
                    {(() => {
                        const waitData = getWaitByPartySizeChartData(data);
                        const hasData = Array.isArray(waitData) && waitData.some((r: any) => Number(r?.w) > 0);
                        return hasData
                            ? <BarChart data={waitData} valueKey="w" displayValueKey="waitLabel" labelKey="s" height={140} color={T.orange} threshold={30}/>
                            : <SectionEmpty kpiName="wait time by party size" minHeight={160} />;
                    })()}
                </ChartCard>
            </div>
            {/* Check-In Volume Heatmap — commented out
            <ChartCard title="Check-In Volume Heatmap" subtitle="Parties per hour × day of week — darker = higher volume" flex={1} minHeight={0}>
                <div style={{ display:"flex" }}>
                    <div style={{ display:"flex", flexDirection:"column", gap:3, marginRight:8, paddingTop:22 }}>
                        {days.map(d => <div key={d} style={{ height:20, display:"flex", alignItems:"center", fontSize:9, color:T.t3, width:28 }}>{d}</div>)}
                    </div>
                    <div style={{ flex:1 }}>
                        <div style={{ display:"flex", gap:3, marginBottom:4 }}>
                            {hourLabels.map((h,i) => <div key={i} style={{ flex:1, textAlign:"center", fontSize:9, color:T.t3 }}>{h}</div>)}
                        </div>
                        {days.map((day,di) => (
                            <div key={day} style={{ display:"flex", gap:3, marginBottom:3 }}>
                                {hours.map((_,hi) => { const v=(heat[di] && heat[di][hi]) || 0; const intensity=maxHeat ? v/maxHeat : 0; return (
                                    <div key={hi} style={{ flex:1, height:20, borderRadius:3, background:`rgba(210,58,59,${0.06+intensity*0.59})`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:8, fontWeight:intensity>0.7?600:400, color:intensity>0.7?"#fff":"transparent", cursor:"pointer" }}>{v}</div>
                                ); })}
                            </div>
                        ))}
                    </div>
                </div>
            </ChartCard>
            */}
            <div className="rpt-flex-row rpt-gap-16">
                {/* Cancellation Trend — commented out
                <ChartCard title="Cancellation Trend" subtitle="Daily cancellations (bars) & rate % — click to drill" flex={2}>
                    <div onClick={()=>onDrill("cancels")} style={{ cursor:"pointer" }}>
                        <BarChart data={(data?.cancelTrendFull || []).map(d=>({d:d.d,v:d.cancels}))} valueKey="v" labelKey="d" height={120} color={T.red}/>
                        <div style={{ display:"flex", gap:6, marginTop:4 }}>
                            {(data?.cancelTrendFull || []).map((d,i)=><div key={i} style={{ flex:1, textAlign:"center", fontSize:9, color:T.t3 }}>{d.rate}%</div>)}
                        </div>
                    </div>
                </ChartCard>
                */}
                <ChartCard id="checkin-section-dinein" title="Dine-In Duration by Party Size" subtitle="Avg minutes at table — click to drill" flex={1} drillable={allowDrill} onTap={allowDrill ? ()=>onDrill("dinein", "checkin-section-dinein") : undefined}>
                    <HourlyBarChart
                        data={(data?.dineinBySize || []).map((r: any) => ({ label: String(r.size ?? ''), value: Number(r.duration) || 0 }))}
                        barColor={T.green}
                        chartHeight={200}
                        kpiName="dine-in duration by party size"
                    />
                </ChartCard>
            </div>
            </>
            )}
        </div>
    );
}

/* Desktop drill screens */
function DDrillCheckins({ data }: any) {
    const checkinsKpi = data?.kpis?.find(k => k.key === "checkins");
    const val = checkinsKpi?.value ?? "0";
    const dailyRows = data?.dailyCheckins || [];
    const isSingleDay = dailyRows.length <= 1;
    return (
        <div className="rpt-page">
            {/* Prior period, Change, Avg/Day commented out */}
            <DSummaryStrip items={[{label:"This Period",value:val}]}/>
            {!isSingleDay && (
            <div className="rpt-flex-row rpt-gap-16">
                <ChartCard title="Daily Check-In Volume" subtitle="Parties per day this week" flex={2}>
                    <HourlyBarChart
                        data={dailyRows.map((r: any) => ({ label: String(r.d ?? ''), value: Number(r.checkins) || 0 }))}
                        barColor={T.accent}
                        chartHeight={160}
                        kpiName="daily check-in volume"
                    />
                </ChartCard>
                {/* By Channel — hidden for all dates
                <ChartCard title="By Channel" subtitle="Check-in source breakdown" flex={1}>
                    <ProgressList data={(data?.channels || []).map(c=>({name:c.name,count:c.value}))} labelKey="name" valueKey="count" color={T.accent}/>
                </ChartCard>
                */}
            </div>
            )}
            <ChartCard title="By Hour of Day" subtitle="When walk-ins arrive" flex={1} minHeight={180}>
                <HourlyBarChart
                    data={(data?.hourly || []).map((r: any) => ({ label: String(r.h ?? ''), value: Number(r.checkins) || 0 }))}
                    barColor={T.accent}
                    chartHeight={140}
                    kpiName="by hour of day"
                />
            </ChartCard>
        </div>
    );
}

function DDrillGuests({ data }: any) {
    const guestsKpi = data?.kpis?.find(k => k.key === "guests");
    const val = guestsKpi?.value ?? "0";
    const dailyRows = data?.dailyCheckins || [];
    const isSingleDay = dailyRows.length <= 1;
    return (
        <div className="rpt-page">
            {/* Prior period, Change, Avg/Day commented out */}
            <DSummaryStrip items={[{label:"This Period",value:val}]}/>
            <GuestVolumeTrend data={data?.guestVolumeTrend || []} hourlySummary={data?.hourlySummary || []} height={180} compact={true}/>
            <div className="rpt-flex-row rpt-gap-16">
                {!isSingleDay && (
                <ChartCard title="Daily Guest Volume" subtitle="Total guests per day" flex={1}>
                    <BarChart data={dailyRows} valueKey="guests" labelKey="d" height={160} color={T.green}/>
                </ChartCard>
                )}
            </div>
        </div>
    );
}

function DDrillAvgParty({ data }: any) {
    const partySizeData = data?.partySize || []; const total = partySizeData.reduce((s,d)=>s+d.count,0);
    const avgPartyKpi = data?.kpis?.find(k => k.key === "avgParty");
    const mostCommon = partySizeData.length ? partySizeData.reduce((a,b) => (b.count > a.count ? b : a), partySizeData[0]) : null;
    return (
        <div className="rpt-page">
            <DSummaryStrip items={[{label:"Avg Party Size",value:avgPartyKpi?.value ?? "—"},{label:"Most Common",value:mostCommon ? `Party of ${mostCommon.size}` : "—"},{label:"Total Parties",value:total}]}/>
            <ChartCard title="Party Size Distribution" subtitle="Count and percentage share by group size" flex={1} minHeight={0}>
                <ProgressList data={partySizeData.map(d=>({label:`Party of ${d.size}`,count:d.count}))} labelKey="label" valueKey="count" color={T.green} labelWidth={100}/>
            </ChartCard>
            <div className="rpt-flex-row rpt-gap-16">
                <ChartCard title="Wait Time by Party Size" subtitle="Avg wait (min) — red if > 30 min" flex={1} minHeight={0} alignSelf="flex-start">
                    <BarChart data={getWaitByPartySizeChartData(data)} valueKey="w" displayValueKey="waitLabel" labelKey="s" height={140} color={T.orange} threshold={30}/>
                    <div style={{ display:"flex", alignItems:"center", gap:4, marginTop:8, justifyContent:"flex-end" }}>
                        <div style={{ width:10, height:3, background:T.accent, borderRadius:1 }}/><span style={{ fontSize:9, color:T.accent }}>Over 30 min</span>
                    </div>
                </ChartCard>
                <ChartCard title="Dine-In Duration by Party Size" subtitle="Avg minutes at table per group" flex={1}>
                    <HourlyBarChart
                        data={(data?.dineinBySize || []).map((d: any) => ({ label: String(d.size ?? ''), value: Number(d.duration) || 0 }))}
                        barColor={T.green}
                        chartHeight={200}
                        kpiName="dine-in duration by party size"
                    />
                </ChartCard>
            </div>
        </div>
    );
}

function DDrillCancels({ data }: any) {
    const cancelKpi = data?.kpis?.find(k => k.key === "cancels");
    const totalCancelKpi = data?.kpis?.find(k => k.key === "totalCancel");
    return (
        <div className="rpt-page">
            {/* Keep only Total and Rate — Change, Daily Trend, By Channel, By Hour commented out */}
            <DSummaryStrip items={[{label:"Total Cancellations",value:totalCancelKpi?.value ?? "0",color:T.red},{label:"Cancel Rate",value:cancelKpi?.value ?? "—"}]}/>
            {/* Daily Cancellation Trend, By Channel, Cancellations by Hour — commented out
            <div className="rpt-flex-row rpt-gap-16">
                <ChartCard title="Daily Cancellation Trend" subtitle="Cancellations per day with rate %" flex={2}>
                    <BarChart data={(data?.cancelTrendFull || []).map(d=>({d:d.d,v:d.cancels}))} valueKey="v" labelKey="d" height={140} color={T.red}/>
                    <div style={{ display:"flex", gap:6, marginTop:4 }}>
                        {(data?.cancelTrendFull || []).map((d,i)=><div key={i} style={{ flex:1, textAlign:"center", fontSize:9, color:T.t3 }}>{d.rate}%</div>)}
                    </div>
                </ChartCard>
                <ChartCard title="By Channel" subtitle="Which channels lose the most?" flex={1}>
                    <CancelByChannelList channels={data?.cancelByChannel || []}/>
                </ChartCard>
            </div>
            <ChartCard title="Cancellations by Hour" subtitle="When do walk-aways happen?" flex={1} minHeight={180}>
                <BarChart data={data?.cancelByHour || []} valueKey="c" labelKey="h" height={120} color={T.red}/>
            </ChartCard>
            */}
        </div>
    );
}

function DDrillWaitTime({ data }: any) {
    const waitKpi = data?.kpis?.find(k => k.key === "waitTime");
    return (
        <div className="rpt-page">
            {/* Shortest Day, Longest Day commented out */}
            <DSummaryStrip items={[{label:"Avg Wait",value:waitKpi?.value ?? "0 min"}]}/>
            <div className="rpt-flex-row rpt-gap-16">
                {/* Wait Time by Day of Week — commented out
                <ChartCard title="Wait Time by Day of Week" subtitle="Avg minutes from check-in to seating — red > 25 min" flex={1}>
                    <BarChart data={data?.waitByDay || []} valueKey="wait" labelKey="d" height={160} color={T.orange} threshold={25}/>
                </ChartCard>
                */}
                <ChartCard title="Avg Wait Time By groups" subtitle="Grouped check-ins and wait-time ranges" flex={1}>
                    <AvgWaitByGroupTable rows={data?.avgWaitTimeByGroup || []} />
                </ChartCard>
            </div>
            {/* Weekday / Weekend compare cards — commented out
            <CompareCards items={[
                {label:"Weekday",value:"—",sub:"Mon–Fri average",highlight:false},
                {label:"Weekend",value:"—",sub:"Sat–Sun average",highlight:true,softBg:T.orangeSoft,accentColor:T.orange},
            ]}/>
            */}
        </div>
    );
}

function DDrillDineIn({ data, refetch }: any) {
    const dineinRows = (data?.dineinBySize || []).map((d: any) => ({ label: `Party of ${d.size}`, dur: d.duration }));
    const hasData = dineinRows.length > 0;
    // const dineInKpi = data?.kpis?.find((k: any) => k.key === "dinein");
    return (
        <div className="rpt-page">
            {/* <DSummaryStrip items={[{ label: "Avg Dine-In Duration", value: dineInKpi?.value ?? "—" }]} /> */}
            <ChartCard title="Duration by Party Size" subtitle="Avg minutes at table per group size" flex={1} minHeight={180}>
                {hasData
                    ? <ProgressList data={dineinRows} labelKey="label" valueKey="dur" valueSuffix=" min" color={T.green} />
                    : <SectionEmpty kpiName="dine-in duration by party size" minHeight={160} />
                }
            </ChartCard>
        </div>
    );
}

function CheckinReportDesktopContent({ data, loading, datePeriod, setDatePeriod, isToday, locationId }: any) {
    const setTopBarProps = useDesktopTopBar();
    const [drillKey, setDrillKey] = useState<any>(null);
    const scrollTargetRef = useRef<string | null>(null);
    const handleDrill = useCallback((key: string, sourceId?: string) => {
        scrollTargetRef.current = sourceId || `checkin-kpi-${key}`;
        setDrillKey(key);
    }, []);
    const goBack = useCallback(() => setDrillKey(null), []);

    useEffect(() => {
        if (drillKey) {
            scrollPageToTop();
            return;
        }
        if (scrollTargetRef.current) {
            const targetId = scrollTargetRef.current;
            scrollTargetRef.current = null;
            const timer = setTimeout(() => {
                scrollToTarget(targetId);
            }, 400);
            return () => clearTimeout(timer);
        }
    }, [drillKey]);
    const drillMap = {
        checkins: <DDrillCheckins data={data} />,
        guests: <DDrillGuests data={data} />,
        avgParty: <DDrillAvgParty data={data} />,
        cancels: <DDrillCancels data={data} />,
        waitTime: <DDrillWaitTime data={data} />,
        dinein: <DDrillDineIn data={data} />,
    };

    useEffect(() => {
        setTopBarProps({
            breadcrumbs: drillKey ? "Reports › Check-In Analytics" : "Reports",
            title: drillKey ? DRILL_TITLES[drillKey] : "Check-In Analytics",
            dateFilters: drillKey ? undefined : {
                value: datePeriod,
                onChange: setDatePeriod,
            },
            leftAddon: drillKey ? (
                <div onClick={goBack} style={{ width: 28, height: 28, borderRadius: 6, background: T.hover, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", marginRight: 4 }}>
                    <span className="material-symbols-rounded" style={{ fontSize: 16, color: T.t2 }}>arrow_back</span>
                </div>
            ) : null,
            onExport: () => {},
        });
    }, [setTopBarProps, drillKey, datePeriod]);

    return loading
        ? <CheckinDesktopShimmer isToday={isToday} />
        : (drillKey ? drillMap[drillKey] : <DMainContent data={data} onDrill={handleDrill} isToday={isToday} datePeriod={datePeriod} locationId={locationId} />);
}

function CheckinDesktopView({ data, loading, datePeriod, setDatePeriod, isToday, locationId }: any) {
    return (
        <DesktopLayout>
            <div
                className="checkin-report-root"
                style={{
                    ["--checkin-bg" as any]: T.bg,
                    ["--checkin-card" as any]: T.card,
                    ["--checkin-shadow" as any]: T.shadow,
                    ["--checkin-card-shadow" as any]: T.cardShadow,
                    ["--checkin-font" as any]: T.font,
                }}
            >
                <CheckinReportDesktopContent data={data} loading={loading} datePeriod={datePeriod} setDatePeriod={setDatePeriod} isToday={isToday} locationId={locationId} />
            </div>
        </DesktopLayout>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   ENTRY POINT
   ═══════════════════════════════════════════════════════════════════════════ */
export default function CheckinReport() {
    const isDesktop = useIsDesktop();
    const { overview, loading, error, refetch, datePeriod, setDatePeriod, isToday, locationId } = useCheckInOverview(getInitialReportDateFilter("Today"));
    const data = useMemo(() => deriveCheckInData(overview, { isToday }), [overview, isToday]);
    if (isDesktop) {
        if (!locationId) return <div style={{ padding:24, textAlign:"center", color:T.t3, fontFamily:T.font }}>Select a location to view Check-In report.</div>;
        if (error) return (
            <DesktopLayout>
                <div style={{ display:"flex", justifyContent:"center", alignItems:"center", minHeight:"60vh" }}>
                    <SectionError kpiName="check-in analytics" onRetry={refetch} size="medium" minHeight={320} />
                </div>
            </DesktopLayout>
        );
        return <CheckinDesktopView data={data} loading={loading} datePeriod={datePeriod} setDatePeriod={setDatePeriod} isToday={isToday} locationId={locationId} />;
    }
    return <MobileLayout data={data} loading={loading} error={error} refetch={refetch} datePeriod={datePeriod} setDatePeriod={setDatePeriod} isToday={isToday} locationId={locationId} />;
}
